import {
  DEFAULT_INSTRUMENT_SLOT_COUNT,
  ensureClipTrackCount,
  findPattern,
  getRowMarker,
  patternSteps,
  rowMarkerColorHex,
  setRowMarker,
  clampRowMarkerColor,
  formatNoteName,
  type ClipIR,
  type InstrumentSlot,
  type Pattern,
  type RowMarker,
} from "../../../../src/lib/audio";
import {
  getAdamCommandHost,
  registerAdamTrackerBridge,
  SetCellsCommand,
  SetRowMarkerCommand,
  SetTrackNameCommand,
  SetPatternMuteCommand,
  SetSoloCommand,
  type AdamCommandHost,
  type AdamCommandContext,
  type AdamCommandResult,
  type AdamSelectionState,
  type CellPatch,
} from "../commands";
import { getLabActions } from "../labActions";
import {
  locateGlobalStep,
  locateViewStep,
  viewStepCount,
} from "../lib/patternLoop";
import {
  analyzeSelectionTools,
  rampHexValues,
  randomHexValues,
} from "../lib/selectionRamp";
import {
  CELL_WIDTH,
  COL_OVERSCAN,
  NOTE_WIDTH,
  TRACKER_ROW_HEIGHT_PX,
  TRACKER_TRACK_WIDTH_CH,
  VOL_WIDTH,
  computeVisibleRange as computeVisibleRangePure,
  computeVisibleTrackRange as computeVisibleTrackRangePure,
  cursorFromFlat as cursorFromFlatPure,
  decodeCell,
  encodeCell,
  fieldForOffset,
  flatOf as flatOfPure,
  clampNoteOctave,
  DEFAULT_NOTE_OCTAVE,
  isNoteLetter,
  noteFieldHasOctave,
  padField,
  setGlyph,
  type TrackerCursor,
} from "../lib/trackerCell";
import { pianoKeyMidi, pianoKeyRemembersOctave } from "../lib/pianoTyping";
import type { TrackerGridCallbacks, TrackerGridHandle } from "./TrackerGridHandle";

export type PaintControllerOptions = TrackerGridCallbacks & {
  /** Focus + key host (tabIndex). */
  focusHost: HTMLElement;
  /** Scroll container (.tracker-wrap). */
  wrap: HTMLElement;
  /** Table already in the DOM (thead is React-owned). */
  table: HTMLTableElement;
  /** Fired when mute/solo or track names change (optional host chrome). */
  onHeaderChange?: () => void;
  /** Floating selection toolbar (ramp, …); null when hidden. */
  onSelectionUi?: (state: SelectionToolbarState | null) => void;
};

export type SelectionToolbarState = {
  /** Viewport rect of the cursor cell (toolbar anchor). */
  anchor: { left: number; top: number; right: number; bottom: number };
  /** Lerp first → last (needs hex bottom). */
  canRamp: boolean;
  /** Stamp top hex across every selected row. */
  canFillTop: boolean;
};

type TrackSlot = {
  td: HTMLTableCellElement;
  chars: HTMLElement[];
  /** Absolute track index currently bound into this recycled slot, or -1. */
  track: number;
};

type PooledRow = {
  tr: HTMLTableRowElement;
  stepCell: HTMLTableCellElement;
  leftPad: HTMLTableCellElement;
  leftPadInner: HTMLDivElement;
  rightPad: HTMLTableCellElement;
  rightPadInner: HTMLDivElement;
  /** Recycled track cells for the current column window (index 0 = cStart). */
  slots: TrackSlot[];
  step: number;
};

/**
 * Hot-path paint + edit controller for the tracker grid.
 * Glyph typing, playhead, cursor, selection, and row pool stay off React reconcile.
 */
export class PaintController implements TrackerGridHandle {
  private clip: ClipIR = {
    bpm: 120,
    stepDivision: 16,
    patterns: [],
    scenes: [],
    order: [],
    loop: true,
  };
  private activePatternId = "P0";
  /** Ordered pattern ids shown stacked in the grid (loop range). */
  private viewPatternIds: string[] = ["P0"];
  /** [globalStep][track] fixed-width edit buffers — source of truth while typing. */
  private buffers: string[][] = [];
  private cursor: TrackerCursor = { step: 0, track: 0, offset: 0 };
  /** Selection anchor (mousedown / shift-start), flat = track * CELL_WIDTH + offset. */
  private selAnchor: { step: number; flat: number } | undefined;
  private selFocus: { step: number; flat: number } | undefined;
  private dragging = false;
  /** Track-header column reorder (HTML5 DnD). */
  private headerDragFrom: number | null = null;
  /** Latest pointer while drag-selecting (for edge auto-scroll). */
  private dragPointer: { x: number; y: number } | undefined;
  /** rAF id for continuous scroll when the pointer sits in an edge zone. */
  private dragScrollRaf = 0;
  /** Command-pattern undo host (patches, not buffer snapshots). */
  private readonly host: AdamCommandHost;
  /** Recording sparse cell patches for the current edit burst. */
  private recording = false;
  private readonly pendingPatches = new Map<string, CellPatch>();
  private editBeforeSelection: AdamSelectionState | undefined;
  private patchMergeGen: number | undefined;
  private typingGen = 0;
  private typingCoalesce = false;
  private typingCoalesceTimer: ReturnType<typeof setTimeout> | undefined;
  private followPlayhead = false;
  /** Coalesce follow-scroll to one layout pass per animation frame. */
  private followScrollRaf: number | undefined;
  private pendingFollowRow: number | undefined;
  private armMode = false;
  /** Piano keys currently held for arm-mode audition (key → note). */
  private readonly armHeldNotes = new Map<string, { track: number; midi: number }>();
  private playheadRow: number | undefined;
  /** Pattern under the audible playhead (stacked view); drives pattern-dim while playing. */
  private playheadPatternId: string | undefined;
  private readonly wrap: HTMLElement;
  private readonly table: HTMLTableElement;
  private readonly focusHost: HTMLElement;
  private vStart = 0;
  private vEnd = 0;
  /** Visible track window [cStart, cEnd). */
  private cStart = 0;
  private cEnd = 0;
  private scrollRaf = 0;
  /**
   * Nestable suppress for programmatic scrollTop / pad relayout.
   * A boolean was wrong: setScrollTop + syncWindow each scheduled a rAF clear,
   * so the first clear re-enabled scroll events while pads were still settling —
   * follow-playhead then desynced topPad from the pooled rows (empty gap).
   */
  private suppressScrollDepth = 0;
  private trackCount = DEFAULT_INSTRUMENT_SLOT_COUNT;
  private cachedTrackWidthPx = 0;
  private cachedStepWidthPx = 0;
  private lastStepDigits = -1;
  private readonly muted: boolean[] = Array.from({ length: DEFAULT_INSTRUMENT_SLOT_COUNT }, () => false);
  private readonly solo: boolean[] = Array.from({ length: DEFAULT_INSTRUMENT_SLOT_COUNT }, () => false);
  /** Project-global track tints (`null` = unset). */
  private trackColors: Array<number | null> = Array.from(
    { length: DEFAULT_INSTRUMENT_SLOT_COUNT },
    () => null,
  );
  /** Recycled body rows — rebound on scroll instead of rebuild. */
  private readonly pool: PooledRow[] = [];
  private topPad: HTMLTableRowElement | undefined;
  private bottomPad: HTMLTableRowElement | undefined;
  private topPadInner: HTMLDivElement | undefined;
  private bottomPadInner: HTMLDivElement | undefined;
  private readonly rowByStep = new Map<number, PooledRow>();
  private cursorCh: HTMLElement | undefined;
  private cursorRowEl: HTMLTableRowElement | undefined;
  private playheadRowEl: HTMLTableRowElement | undefined;
  private readonly selectedChs: HTMLElement[] = [];
  private lastNotifiedStep: number | undefined;
  private lastNotifiedTrack: number | undefined;
  private lastNotifiedPlayhead: number | undefined;
  private changeTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly rowTip: HTMLElement;
  private rowTipHideTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly onWindowMouseMove: (event: MouseEvent) => void;
  private readonly onWindowMouseUp: () => void;
  private readonly onWindowKeyDown: (event: KeyboardEvent) => void;
  private readonly onWindowKeyUp: (event: KeyboardEvent) => void;
  /** Last octave digit typed / auto-filled (default 4). */
  private lastOctave = DEFAULT_NOTE_OCTAVE;
  /** Last pitched note edited (MIDI); `,` in the note field repeats it. */
  private lastNoteMidi: number | undefined;

  constructor(private readonly options: PaintControllerOptions) {
    this.focusHost = options.focusHost;
    this.wrap = options.wrap;
    this.table = options.table;
    this.host = getAdamCommandHost();
    registerAdamTrackerBridge({
      createContext: () => this.createCommandContext(),
      commit: (result, selection) => this.commitCommandResult(result, selection),
      prepareHistoryNav: () => this.prepareHistoryNav(),
    });
    this.focusHost.tabIndex = 0;
    this.focusHost.classList.add("tracker-host");
    if (!this.table.tHead) {
      this.table.createTHead();
    }
    if (!this.table.tBodies[0]) {
      this.table.createTBody();
    }

    this.rowTip = document.createElement("div");
    this.rowTip.className = "row-label-tip";
    this.rowTip.hidden = true;
    this.rowTip.setAttribute("role", "tooltip");
    document.body.append(this.rowTip);

    this.onWindowMouseMove = event => this.onDragMove(event);
    this.onWindowMouseUp = () => {
      this.dragging = false;
      this.stopDragScrollLoop();
      window.removeEventListener("mousemove", this.onWindowMouseMove);
      window.removeEventListener("mouseup", this.onWindowMouseUp);
    };
    this.onWindowKeyDown = event => this.onGlobalKeyDown(event);
    this.onWindowKeyUp = event => this.onArmKeyUp(event);

    this.focusHost.addEventListener("keydown", event => this.onKeyDown(event));
    this.focusHost.addEventListener("mousedown", event => this.onMouseDown(event));
    // mousedown preventDefault does not suppress the separate contextmenu event.
    this.table.addEventListener("contextmenu", event => this.onTrackerContextMenu(event));
    this.table.addEventListener("pointerover", event => this.onStepPointerOver(event));
    this.table.addEventListener("pointerout", event => this.onStepPointerOut(event));
    this.wrap.addEventListener("scroll", () => {
      this.hideRowTip();
      this.onWrapScroll();
    }, { passive: true });
    if (typeof ResizeObserver !== "undefined") {
      new ResizeObserver(() => this.syncWindow()).observe(this.wrap);
    }
    // Capture so Safari doesn't reopen the last closed tab on ⌘Z
    window.addEventListener("keydown", this.onWindowKeyDown, true);
    window.addEventListener("keyup", this.onWindowKeyUp, true);
  }

  dispose(): void {
    registerAdamTrackerBridge(null);
    this.releaseArmAudition();
    this.hideRowTip();
    this.options.onSelectionUi?.(null);
    window.removeEventListener("keydown", this.onWindowKeyDown, true);
    window.removeEventListener("keyup", this.onWindowKeyUp, true);
    window.removeEventListener("mousemove", this.onWindowMouseMove);
    window.removeEventListener("mouseup", this.onWindowMouseUp);
    this.rowTip.remove();
    if (this.changeTimer !== undefined) {
      clearTimeout(this.changeTimer);
    }
    if (this.typingCoalesceTimer !== undefined) {
      clearTimeout(this.typingCoalesceTimer);
    }
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
    }
    this.stopDragScrollLoop();
    if (this.followScrollRaf !== undefined) {
      cancelAnimationFrame(this.followScrollRaf);
      this.followScrollRaf = undefined;
      this.pendingFollowRow = undefined;
    }
  }

  getClip(): ClipIR {
    this.flushBuffersToClip();
    this.flushChangeNow();
    return structuredClone(this.clip);
  }

  setClip(clip: ClipIR, options?: { resetHistory?: boolean; activePatternId?: string }): void {
    if (this.changeTimer !== undefined) {
      clearTimeout(this.changeTimer);
      this.changeTimer = undefined;
    }
    this.clip = structuredClone(clip);
    const inferred = Math.max(
      1,
      ...this.clip.patterns.map(p => p.tracks.length),
    );
    ensureClipTrackCount(this.clip, inferred);
    this.syncTrackCount(inferred);
    const requested = options?.activePatternId ?? this.activePatternId;
    this.activePatternId = findPattern(this.clip, requested)?.id ?? this.clip.patterns[0]?.id ?? "P0";
    const kept = this.viewPatternIds
      .map(id => findPattern(this.clip, id)?.id)
      .filter((id): id is string => typeof id === "string");
    this.viewPatternIds = kept.length > 0
      ? (kept.includes(this.activePatternId) ? kept : [this.activePatternId, ...kept.filter(id => id !== this.activePatternId)])
      : [this.activePatternId];
    // Prefer contiguous order from clip.patterns when possible.
    const ordered = this.clip.patterns
      .map(p => p.id)
      .filter(id => this.viewPatternIds.includes(id));
    if (ordered.length === this.viewPatternIds.length) {
      this.viewPatternIds = ordered;
    }
    this.rebuildBuffersFromClip();
    this.cursor = {
      step: Math.min(this.cursor.step, Math.max(0, this.stepCount() - 1)),
      track: Math.min(this.slotCount() - 1, Math.max(0, this.cursor.track)),
      offset: Math.min(CELL_WIDTH - 1, Math.max(0, this.cursor.offset)),
    };
    this.syncActiveFromCursor({ silent: true });
    this.syncMutedFromActivePattern();
    if (options?.resetHistory !== false) {
      this.clearHistory();
    }
    this.render();
  }

  getActivePatternId(): string {
    return this.activePatternId;
  }

  getViewPatternIds(): string[] {
    return [...this.viewPatternIds];
  }

  /**
   * Show these patterns stacked in the grid (loop range).
   * Preserves cursor on the same pattern/local step when possible, and keeps
   * the current active pattern when it remains inside the new stack.
   */
  setViewPatternIds(patternIds: string[]): void {
    const next = patternIds
      .map(id => findPattern(this.clip, id)?.id)
      .filter((id): id is string => typeof id === "string");
    if (next.length === 0) {
      const fallback = findPattern(this.clip, this.activePatternId)?.id
        ?? this.clip.patterns[0]?.id
        ?? "P0";
      next.push(fallback);
    }
    const same =
      next.length === this.viewPatternIds.length
      && next.every((id, i) => id === this.viewPatternIds[i]);
    if (same) {
      return;
    }
    this.flushBuffersToClip();
    const before = locateViewStep(this.clip.patterns, this.viewPatternIds, this.cursor.step);
    const prevActive = this.activePatternId;
    this.viewPatternIds = next;
    if (!this.viewPatternIds.includes(this.activePatternId)) {
      this.activePatternId = this.viewPatternIds[0]!;
    }
    this.rebuildBuffersFromClip();
    let global = 0;
    if (this.viewPatternIds.includes(prevActive)) {
      // Prefer keeping edit focus on the prior active pattern when it remains
      // in the stack (avoids jumping to the first stacked pattern on boot /
      // brace expand when the cursor remap resolved to a different id).
      const local = before && before.patternId === prevActive ? before.localStep : 0;
      global = locateGlobalStep(
        this.clip.patterns,
        this.viewPatternIds,
        prevActive,
        local,
      ) ?? 0;
      this.activePatternId = prevActive;
    } else if (before) {
      global = locateGlobalStep(
        this.clip.patterns,
        this.viewPatternIds,
        before.patternId,
        before.localStep,
      ) ?? 0;
    }
    this.cursor = {
      step: Math.min(Math.max(0, global), Math.max(0, this.stepCount() - 1)),
      track: this.cursor.track,
      offset: this.cursor.offset,
    };
    this.syncActiveFromCursor({ silent: true });
    this.syncMutedFromActivePattern();
    this.render();
    this.options.onActivePatternChange?.(this.activePatternId);
    this.options.onPositionChange?.();
  }

  locateViewStep(globalStep: number) {
    return locateViewStep(this.clip.patterns, this.viewPatternIds, globalStep);
  }

  locateGlobalStep(patternId: string, localStep: number): number | null {
    return locateGlobalStep(this.clip.patterns, this.viewPatternIds, patternId, localStep);
  }

  /** Switch which pattern is “current” (bright). Keeps stacked view when possible. */
  setActivePatternId(patternId: string, options?: { silent?: boolean }): void {
    const next = findPattern(this.clip, patternId);
    if (!next) {
      return;
    }
    if (next.id === this.activePatternId && this.viewPatternIds.includes(next.id)) {
      return;
    }
    this.flushBuffersToClip();
    this.activePatternId = next.id;
    let patternStart = 0;
    if (!this.viewPatternIds.includes(next.id)) {
      this.viewPatternIds = [next.id];
      this.rebuildBuffersFromClip();
      this.cursor = {
        step: Math.min(this.cursor.step, Math.max(0, this.stepCount() - 1)),
        track: this.cursor.track,
        offset: this.cursor.offset,
      };
      patternStart = 0;
    } else {
      patternStart = locateGlobalStep(
        this.clip.patterns,
        this.viewPatternIds,
        next.id,
        0,
      ) ?? 0;
      // Keep cursor inside the newly active pattern if it was elsewhere.
      const at = locateViewStep(this.clip.patterns, this.viewPatternIds, this.cursor.step);
      if (!at || at.patternId !== next.id) {
        this.cursor = {
          step: patternStart,
          track: this.cursor.track,
          offset: this.cursor.offset,
        };
      }
    }
    this.syncMutedFromActivePattern();
    this.render();
    // Pin the pattern's first row to the top — nearest-edge scroll leaves it
    // stuck on the last visible row when jumping downward in a stacked view.
    this.scrollStepToTop(patternStart);
    if (!options?.silent) {
      this.options.onActivePatternChange?.(this.activePatternId);
    }
    this.options.onPositionChange?.();
  }

  focus(): void {
    this.focusHost.focus();
  }

  /** Current cursor row (0-based step). */
  getCursorStep(): number {
    return this.cursor.step;
  }

  /** Current cursor instrument column (0..7). */
  getCursorTrack(): InstrumentSlot {
    return this.cursor.track;
  }

  getPlayheadRow(): number | undefined {
    return this.playheadRow;
  }

  /** Jump to row 0 / track 0 (Shift+Enter). Collapsed selection — no full-row select. */
  jumpToFirstRow(): void {
    if (this.stepCount() <= 0) {
      return;
    }
    this.endTypingCoalesce();
    this.cursor = { step: 0, track: 0, offset: 0 };
    this.selAnchor = { step: 0, flat: 0 };
    this.selFocus = { step: 0, flat: 0 };
    this.paintCursor();
    this.scrollCursorIntoView();
    this.focus();
  }

  /** Place cursor at step/track/offset (default home cell). */
  jumpToCell(
    step = 0,
    track: InstrumentSlot = 0,
    offset = 0,
    options?: { focus?: boolean },
  ): void {
    if (this.stepCount() <= 0) {
      return;
    }
    this.endTypingCoalesce();
    const next = {
      step: Math.min(this.stepCount() - 1, Math.max(0, step)),
      track: Math.min(this.slotCount() - 1, Math.max(0, track)) as InstrumentSlot,
      offset: Math.min(CELL_WIDTH - 1, Math.max(0, offset)),
    };
    this.cursor = next;
    const flat = this.flatOf(next);
    this.selAnchor = { step: next.step, flat };
    this.selFocus = { step: next.step, flat };
    this.paintCursor();
    this.scrollCursorIntoView();
    if (options?.focus !== false) {
      this.focus();
    }
  }

  getMuted(): boolean[] {
    return this.muted.slice(0, this.slotCount());
  }

  getSolo(): boolean[] {
    return this.solo.slice(0, this.slotCount());
  }

  /** Push current state before an external edit (e.g. pattern length change). */
  checkpoint(): void {
    // Topology commands land in P4 — do not snapshot the clip.
    this.endTypingCoalesce();
  }

  /**
   * Highlight the audible playhead row. Only touches visible DOM.
   * When follow-playhead is on, jump-scrolls the row to the vertical center of the viewport.
   */
  setPlayheadRow(row: number | undefined): void {
    const prevPattern = this.playheadPatternId;
    this.playheadRow = row;
    this.lastNotifiedPlayhead = row;
    if (row === undefined) {
      this.playheadPatternId = undefined;
    } else {
      this.playheadPatternId =
        locateViewStep(this.clip.patterns, this.viewPatternIds, row)?.patternId;
    }
    // Multi-pattern: dim non-playing patterns so follow tracks the audible pattern,
    // not only the edit-active one.
    if (prevPattern !== this.playheadPatternId) {
      this.paintPatternDim();
    }
    // Always paint immediately (cheap classList) so the highlight moves even when
    // follow-scroll is coalesced to the next animation frame.
    this.paintPlayhead();
    if (this.followPlayhead && row !== undefined) {
      // rAF-coalesce: multi-pattern follow was syncWindow()'ing every step and
      // starving main-thread clockSync / note forwarding into the worker.
      this.scheduleFollowScroll(row);
    }
  }

  setFollowPlayhead(on: boolean): void {
    this.followPlayhead = on;
    if (on && this.playheadRow !== undefined) {
      this.scheduleFollowScroll(this.playheadRow);
    } else if (this.followScrollRaf !== undefined) {
      cancelAnimationFrame(this.followScrollRaf);
      this.followScrollRaf = undefined;
      this.pendingFollowRow = undefined;
    }
  }

  private scheduleFollowScroll(row: number): void {
    this.pendingFollowRow = row;
    if (this.followScrollRaf !== undefined) {
      return;
    }
    this.followScrollRaf = requestAnimationFrame(() => {
      this.followScrollRaf = undefined;
      const target = this.pendingFollowRow;
      this.pendingFollowRow = undefined;
      if (target !== undefined && this.followPlayhead) {
        this.scrollPlayheadToCenter(target);
      }
    });
  }

  /** Pattern that should stay bright in a stacked view (playhead while playing, else edit). */
  private focusPatternId(): string {
    return this.playheadPatternId ?? this.activePatternId;
  }

  isFollowPlayhead(): boolean {
    return this.followPlayhead;
  }

  setArmMode(on: boolean): void {
    this.armMode = on;
    if (!on) {
      this.releaseArmAudition();
    }
  }

  isArmMode(): boolean {
    return this.armMode;
  }

  private paintPlayhead(): void {
    if (this.playheadRowEl) {
      this.playheadRowEl.classList.remove("playhead");
      this.playheadRowEl = undefined;
    }
    if (this.playheadRow === undefined) {
      return;
    }
    const row = this.rowByStep.get(this.playheadRow);
    if (row) {
      row.tr.classList.add("playhead");
      this.playheadRowEl = row.tr;
    }
  }

  private scrollPlayheadToCenter(row: number): void {
    const view = this.wrap.clientHeight;
    const theadH = this.table.tHead?.offsetHeight ?? 0;
    const mid = row * TRACKER_ROW_HEIGHT_PX + TRACKER_ROW_HEIGHT_PX / 2;
    const desired = mid - (view - theadH) / 2;
    const maxScroll = Math.max(0, this.stepCount() * TRACKER_ROW_HEIGHT_PX + theadH - view);
    const next = Math.min(maxScroll, Math.max(0, desired));
    const prevScroll = this.wrap.scrollTop;
    // Already centered — highlight was painted in setPlayheadRow.
    if (Math.abs(next - prevScroll) < 0.5) {
      this.paintPlayhead();
      return;
    }
    // One suppress region around scroll + window sync so nested rAF clears
    // cannot re-enable onWrapScroll mid-relayout.
    // Do not force-rebind: scrollTop change alone updates the virtual window.
    this.beginScrollSuppress();
    this.wrap.scrollTop = next;
    this.syncWindow();
    // Pad swaps can clamp scrollTop; re-apply once the window matches.
    if (Math.abs(this.wrap.scrollTop - next) > 0.5) {
      this.wrap.scrollTop = next;
      this.syncWindow();
    }
    this.endScrollSuppress();
    this.paintPlayhead();
  }

  private beginScrollSuppress(): void {
    this.suppressScrollDepth += 1;
  }

  private endScrollSuppress(): void {
    // Keep suppress through the browser's post-layout scroll events.
    requestAnimationFrame(() => {
      this.suppressScrollDepth = Math.max(0, this.suppressScrollDepth - 1);
    });
  }

  private isScrollSuppressed(): boolean {
    return this.suppressScrollDepth > 0;
  }

  private setScrollTop(y: number): void {
    this.beginScrollSuppress();
    this.wrap.scrollTop = y;
    this.endScrollSuppress();
  }

  private onWrapScroll(): void {
    if (this.isScrollSuppressed()) {
      return;
    }
    if (this.scrollRaf !== 0) {
      return;
    }
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = 0;
      if (!this.isScrollSuppressed()) {
        this.syncWindow();
      }
    });
  }

  private pattern(): Pattern | undefined {
    return findPattern(this.clip, this.activePatternId);
  }

  private patternAtGlobalStep(globalStep: number): Pattern | undefined {
    const ref = locateViewStep(this.clip.patterns, this.viewPatternIds, globalStep);
    return ref ? findPattern(this.clip, ref.patternId) : undefined;
  }

  private stepCount(): number {
    return viewStepCount(this.clip.patterns, this.viewPatternIds);
  }

  private syncActiveFromCursor(options?: { silent?: boolean }): void {
    const ref = locateViewStep(this.clip.patterns, this.viewPatternIds, this.cursor.step);
    if (!ref || ref.patternId === this.activePatternId) {
      return;
    }
    this.activePatternId = ref.patternId;
    this.syncMutedFromActivePattern();
    this.paintPatternDim();
    this.paintTrackHeads();
    // Refresh InstrumentDock mute lamps for the newly active pattern.
    this.options.onMuteSoloChange?.({ muted: this.getMuted(), solo: this.getSolo() });
    if (!options?.silent) {
      this.options.onActivePatternChange?.(this.activePatternId);
    }
  }

  private paintPatternDim(): void {
    const focusId = this.focusPatternId();
    for (const row of this.pool) {
      if (row.step < 0) {
        continue;
      }
      const at = locateViewStep(this.clip.patterns, this.viewPatternIds, row.step);
      row.tr.classList.toggle("pattern-dim", Boolean(at && at.patternId !== focusId));
    }
  }

  private rebuildBuffersFromClip(): void {
    const maxTracks = Math.max(
      1,
      ...this.viewPatternIds.map(id => findPattern(this.clip, id)?.tracks.length ?? 0),
      this.pattern()?.tracks.length ?? 0,
    );
    this.syncTrackCount(maxTracks);
    this.buffers = [];
    for (const patternId of this.viewPatternIds) {
      const pattern = findPattern(this.clip, patternId);
      const steps = Math.max(1, patternSteps(pattern));
      for (let local = 0; local < steps; local++) {
        const row: string[] = [];
        for (let track = 0; track < this.slotCount(); track++) {
          const cell = pattern?.tracks[track]?.cells[local];
          row.push(encodeCell(cell));
        }
        this.buffers.push(row);
      }
    }
  }

  private flushBuffersToClip(): void {
    let global = 0;
    for (const patternId of this.viewPatternIds) {
      const pattern = findPattern(this.clip, patternId);
      if (!pattern) {
        continue;
      }
      const steps = Math.max(1, patternSteps(pattern));
      for (let local = 0; local < steps; local++) {
        for (let track = 0; track < this.slotCount(); track++) {
          const trackObj = pattern.tracks[track];
          const buffer = this.buffers[global]?.[track];
          if (trackObj && buffer) {
            trackObj.cells[local] = decodeCell(buffer);
          }
        }
        global += 1;
      }
    }
  }

  private bufferAt(step: number, track: number): string {
    return this.buffers[step]?.[track] ?? ".".repeat(CELL_WIDTH);
  }


  private writeGlyph(
    step: number,
    track: number,
    offset: number,
    char: string,
    options?: { silent?: boolean },
  ): void {
    const from = this.bufferAt(step, track);
    let current = from.padEnd(CELL_WIDTH, ".").slice(0, CELL_WIDTH);

    if (offset < NOTE_WIDTH) {
      const note = current.slice(0, NOTE_WIDTH).replace(/\./g, " ");
      current = note + current.slice(NOTE_WIDTH);
    }

    const next = current.split("");
    next[offset] = char.slice(0, 1);
    if (offset < NOTE_WIDTH) {
      for (let i = 0; i < NOTE_WIDTH; i++) {
        if (next[i] === ".") {
          next[i] = " ";
        }
      }
    }
    const buffer = next.join("");
    if (this.recording) {
      this.recordCellPatch(step, track, from, buffer);
    }
    this.applyCellBuffer(step, track, buffer, options);
  }

  /** Write a full fixed-width cell buffer into the edit grid + clip. */
  private applyCellBuffer(
    step: number,
    track: number,
    buffer: string,
    options?: { silent?: boolean; rememberOctave?: boolean; rememberNote?: boolean },
  ): void {
    const packed = buffer.padEnd(CELL_WIDTH, ".").slice(0, CELL_WIDTH);
    const row = this.buffers[step] ?? (this.buffers[step] = []);
    row[track] = packed;

    const ref = locateViewStep(this.clip.patterns, this.viewPatternIds, step);
    const pattern = ref ? findPattern(this.clip, ref.patternId) : undefined;
    const trackObj = pattern?.tracks[track];
    if (trackObj && ref) {
      const cell = decodeCell(packed);
      trackObj.cells[ref.localStep] = cell;
      if (options?.rememberNote !== false) {
        this.rememberNoteFromCell(cell, { rememberOctave: options?.rememberOctave });
      }
      if (!options?.silent) {
        this.scheduleChange();
      }
    }

    this.patchCellDom(step, track, packed);
  }

  private recordCellPatch(step: number, track: number, from: string, to: string): void {
    const key = `${step}:${track}`;
    const prev = this.pendingPatches.get(key);
    if (prev) {
      prev.to = to;
      return;
    }
    this.pendingPatches.set(key, { step, track, from, to });
  }

  private patchCellDom(step: number, track: number, buffer: string): void {
    const row = this.rowByStep.get(step);
    const chars = row ? this.charsForTrack(row, track) : undefined;
    if (!chars) {
      return;
    }
    for (let offset = 0; offset < CELL_WIDTH; offset++) {
      const el = chars[offset];
      if (el) {
        setGlyph(el, buffer[offset] ?? ".");
      }
    }
  }

  private scheduleChange(): void {
    if (this.changeTimer !== undefined) {
      clearTimeout(this.changeTimer);
    }
    this.changeTimer = setTimeout(() => {
      this.changeTimer = undefined;
      this.options.onChange(this.clip);
    }, 48);
  }

  private flushChangeNow(): void {
    if (this.changeTimer === undefined) {
      return;
    }
    clearTimeout(this.changeTimer);
    this.changeTimer = undefined;
    this.options.onChange(this.clip);
  }

  private render(): void {
    this.options.onMuteSoloChange?.({ muted: this.getMuted(), solo: this.getSolo() });
    this.vStart = -1;
    this.vEnd = -1;
    this.cStart = -1;
    this.cEnd = -1;
    this.clearPaintRefs();
    this.syncWindow(true);
    this.scrollCursorIntoView();
  }

  private clearPaintRefs(): void {
    this.cursorCh = undefined;
    this.cursorRowEl = undefined;
    this.playheadRowEl = undefined;
    this.selectedChs.length = 0;
  }

  /** Active instrument column count for this project. */
  slotCount(): number {
    return this.trackCount;
  }

  private syncTrackCount(next: number): void {
    const n = Math.max(1, Math.round(next));
    if (n === this.trackCount) {
      while (this.muted.length < n) {
        this.muted.push(false);
        this.solo.push(false);
      }
      while (this.trackColors.length < n) {
        this.trackColors.push(null);
      }
      return;
    }
    this.trackCount = n;
    while (this.muted.length < n) {
      this.muted.push(false);
      this.solo.push(false);
    }
    while (this.trackColors.length < n) {
      this.trackColors.push(null);
    }
    if (this.muted.length > n) {
      this.muted.length = n;
      this.solo.length = n;
    }
    if (this.trackColors.length > n) {
      this.trackColors.length = n;
    }
    // Force row pool rebuild for the new column count.
    this.topPad = undefined;
    this.bottomPad = undefined;
    this.topPadInner = undefined;
    this.bottomPadInner = undefined;
    this.pool.length = 0;
    this.clearPaintRefs();
    this.rowByStep.clear();
    this.cStart = 0;
    this.cEnd = 0;
    this.cachedTrackWidthPx = 0;
    this.cachedStepWidthPx = 0;
    this.lastStepDigits = -1;
  }

  toggleMute(track: number): void {
    // Mute is per active pattern (solo stays global).
    const pattern = this.pattern();
    const trackObj = pattern?.tracks[track];
    if (!trackObj || !pattern) {
      return;
    }
    const from = trackObj.muted === true;
    const to = !from;
    this.endTypingCoalesce();
    this.host.execute(
      new SetPatternMuteCommand(pattern.id, track, from, to),
      {
        beforeSelection: this.captureSelection(),
        afterSelection: this.captureSelection(),
      },
    );
  }

  /** Load header mute lamps from the active (bright) pattern. */
  private syncMutedFromActivePattern(): void {
    const pattern = this.pattern();
    for (let track = 0; track < this.slotCount(); track++) {
      this.muted[track] = pattern?.tracks[track]?.muted === true;
    }
  }

  /** Dim only rows belonging to patterns where this track is muted. */
  private paintPatternMuteDims(track?: number): void {
    const slotIndexStart = this.cStart;
    const slotIndexEnd = this.cEnd;
    for (const row of this.pool) {
      if (row.step < 0) {
        continue;
      }
      const ref = locateViewStep(this.clip.patterns, this.viewPatternIds, row.step);
      const pattern = ref ? findPattern(this.clip, ref.patternId) : this.pattern();
      for (let slotIndex = 0; slotIndex < slotIndexEnd - slotIndexStart; slotIndex++) {
        const t = slotIndexStart + slotIndex;
        if (track !== undefined && t !== track) {
          continue;
        }
        const patternMuted = pattern?.tracks[t]?.muted === true;
        const dimmed = !this.isTrackAudible(t, patternMuted);
        row.slots[slotIndex]?.td.classList.toggle("is-dimmed", dimmed);
      }
    }
  }

  toggleSolo(track: number): void {
    const from = this.solo[track] === true;
    const to = !from;
    this.endTypingCoalesce();
    this.host.execute(
      new SetSoloCommand(track, from, to),
      {
        beforeSelection: this.captureSelection(),
        afterSelection: this.captureSelection(),
      },
    );
  }

  /** Restore mute/solo from persistence / project import. */
  setMuteSolo(muted: boolean[], solo: boolean[], options?: { emit?: boolean }): void {
    for (let track = 0; track < this.slotCount(); track++) {
      this.muted[track] = muted[track] === true;
      this.solo[track] = solo[track] === true;
    }
    this.paintTrackHeads();
    if (options?.emit === false) {
      this.syncHeaderColumns();
      this.options.onHeaderChange?.();
      return;
    }
    this.emitMuteSolo();
  }

  getTrackNames(): string[] {
    const pattern = this.pattern();
    return Array.from({ length: this.slotCount() }, (_, track) =>
      pattern?.tracks[track]?.name ?? `I${track + 1}`,
    );
  }

  renameTrack(track: number, name: string): void {
    this.commitTrackName(track, name);
  }

  setTrackColors(colors: Array<number | null>): void {
    const n = this.slotCount();
    this.trackColors = Array.from({ length: n }, (_, i) => {
      const value = colors[i];
      if (value == null || !Number.isFinite(value)) {
        return null;
      }
      return clampRowMarkerColor(value);
    });
    this.paintTrackTints();
  }

  getTrackColors(): Array<number | null> {
    return this.trackColors.slice(0, this.slotCount());
  }

  private emitMuteSolo(): void {
    this.options.onMuteSoloChange?.({
      muted: this.getMuted(),
      solo: this.getSolo(),
    });
    this.syncHeaderColumns();
    this.options.onHeaderChange?.();
  }

  private paintTrackHeads(): void {
    this.syncHeaderColumns();
    for (let track = 0; track < this.slotCount(); track++) {
      this.paintTrackHead(track);
    }
  }

  /** Dim pool cells for mute/solo — thead chrome is refreshed via syncHeaderColumns. */
  private paintTrackHead(track: number): void {
    this.paintPatternMuteDims(track);
  }

  private trackTintHex(track: number): string | undefined {
    const color = this.trackColors[track];
    if (color == null) {
      return undefined;
    }
    return rowMarkerColorHex(color);
  }

  private applyTrackTint(el: HTMLElement, track: number): void {
    const hex = this.trackTintHex(track);
    if (hex) {
      el.classList.add("has-track-tint");
      el.style.setProperty("--track-tint", hex);
    } else {
      el.classList.remove("has-track-tint");
      el.style.removeProperty("--track-tint");
    }
  }

  /** Refresh track-head tints after trackColors change. */
  private paintTrackTints(): void {
    for (const th of this.table.querySelectorAll("th.track-head")) {
      const track = Number((th as HTMLElement).dataset.track);
      if (Number.isInteger(track)) {
        this.applyTrackTint(th as HTMLElement, track);
      }
    }
    // Body cells are untinted — clear any leftover classes from earlier builds.
    for (const row of this.pool) {
      for (const slot of row.slots) {
        slot.td.classList.remove("has-track-tint");
        slot.td.style.removeProperty("--track-tint");
      }
    }
  }

  private isTrackAudible(track: number, patternMuted = this.muted[track] === true): boolean {
    const anySolo = this.solo.some(Boolean);
    return anySolo ? this.solo[track] === true : !patternMuted;
  }

  private refreshHeader(): void {
    this.syncHeaderColumns();
    this.options.onHeaderChange?.();
  }

  private commitTrackName(track: number, raw: string): void {
    const pattern = this.pattern();
    const slot = pattern?.tracks[track];
    if (!slot) {
      this.refreshHeader();
      return;
    }
    const next = raw.trim().slice(0, 40) || undefined;
    const prev = slot.name;
    if ((next ?? "") === (prev ?? "")) {
      this.refreshHeader();
      return;
    }
    this.endTypingCoalesce();
    this.host.execute(new SetTrackNameCommand(track, prev, next), {
      beforeSelection: this.captureSelection(),
      afterSelection: this.captureSelection(),
    });
  }

  private applyTrackNameAllPatterns(track: number, name: string | undefined): void {
    for (const entry of this.clip.patterns) {
      const trackObj = entry.tracks[track];
      if (!trackObj) {
        continue;
      }
      if (name) {
        trackObj.name = name;
      } else {
        delete trackObj.name;
      }
    }
  }

  private computeVisibleRange(): { start: number; end: number } {
    return computeVisibleRangePure({
      steps: this.stepCount(),
      scrollTop: this.wrap.scrollTop,
      clientHeight: this.wrap.clientHeight,
    });
  }

  private computeVisibleTrackRange(): { start: number; end: number } {
    return computeVisibleTrackRangePure({
      tracks: this.slotCount(),
      scrollLeft: this.wrap.scrollLeft,
      clientWidth: this.wrap.clientWidth,
      trackWidthPx: this.trackWidthPx(),
      stepWidthPx: this.stepWidthPx(),
      overscan: COL_OVERSCAN,
    });
  }

  /** Public for React header column virtualization. */
  getVisibleTrackWindow(): { start: number; end: number; trackWidthPx: number } {
    return {
      start: this.cStart,
      end: this.cEnd,
      trackWidthPx: this.trackWidthPx(),
    };
  }

  private trackWidthPx(): number {
    if (this.cachedTrackWidthPx > 0) {
      return this.cachedTrackWidthPx;
    }
    // Prefer a body cell — header padding can differ before layout settles.
    const sample = this.table.querySelector("td.track-cell") as HTMLElement | null
      ?? this.table.querySelector("th.track-head") as HTMLElement | null;
    const measured = sample?.getBoundingClientRect().width ?? 0;
    if (measured > 1) {
      this.cachedTrackWidthPx = Math.max(1, Math.round(measured));
      return this.cachedTrackWidthPx;
    }
    // Fallback before first cell mounts: 18ch + padding ≈ 20ch at 12px mono.
    this.cachedTrackWidthPx = TRACKER_TRACK_WIDTH_CH * 8 + 16;
    return this.cachedTrackWidthPx;
  }

  private stepWidthPx(): number {
    if (this.cachedStepWidthPx > 0) {
      return this.cachedStepWidthPx;
    }
    const digits = Math.max(2, String(Math.max(0, this.stepCount() - 1)).length);
    this.table.style.setProperty("--step-digits", String(digits));
    // Drop prior px pins so we read the CSS ch-based width, not a squeezed layout.
    const stepHead = this.table.querySelector("th.step-head") as HTMLElement | null;
    if (stepHead) {
      stepHead.style.width = "";
      stepHead.style.minWidth = "";
      stepHead.style.maxWidth = "";
    }
    for (const row of this.pool) {
      row.stepCell.style.width = "";
      row.stepCell.style.minWidth = "";
      row.stepCell.style.maxWidth = "";
    }
    const sample = (this.pool[0]?.stepCell ?? stepHead) as HTMLElement | null;
    if (sample) {
      const computed = Number.parseFloat(getComputedStyle(sample).width);
      if (Number.isFinite(computed) && computed > 1) {
        this.cachedStepWidthPx = Math.max(1, Math.ceil(computed));
        return this.cachedStepWidthPx;
      }
      const measured = sample.getBoundingClientRect().width;
      if (measured > 1) {
        this.cachedStepWidthPx = Math.max(1, Math.ceil(measured));
        return this.cachedStepWidthPx;
      }
    }
    // Fallback: mono ch ≈ 8px at 12px, plus 0.8rem padding + border.
    this.cachedStepWidthPx = digits * 8 + 14;
    return this.cachedStepWidthPx;
  }

  /** Pin a column cell to the cached integer width so layout cannot drift. */
  private applyFixedColWidth(el: HTMLElement, widthPx: number): void {
    const px = `${Math.max(0, Math.round(widthPx))}px`;
    el.style.width = px;
    el.style.minWidth = px;
    el.style.maxWidth = px;
  }

  /** Lock total table width so H-virt pad swaps cannot resize the scrollbar. */
  private syncTableScrollWidth(): void {
    const digits = Math.max(2, String(Math.max(0, this.stepCount() - 1)).length);
    this.table.style.setProperty("--step-digits", String(digits));
    const stepW = this.stepWidthPx();
    const trackW = this.trackWidthPx();
    const total = stepW + this.slotCount() * trackW;
    const px = `${Math.max(0, total)}px`;
    this.table.style.width = px;
    this.table.style.minWidth = px;
    this.table.style.maxWidth = px;
    // Pin step gutter so table-layout:fixed cannot redistribute into tracks.
    const stepHead = this.table.querySelector("th.step-head") as HTMLElement | null;
    if (stepHead) {
      this.applyFixedColWidth(stepHead, stepW);
    }
    for (const row of this.pool) {
      this.applyFixedColWidth(row.stepCell, stepW);
      for (const slot of row.slots) {
        this.applyFixedColWidth(slot.td, trackW);
      }
    }
    for (const th of this.table.querySelectorAll("th.track-head")) {
      this.applyFixedColWidth(th as HTMLElement, trackW);
    }
  }

  private syncWindow(force = false): void {
    const stepDigits = Math.max(2, String(Math.max(0, this.stepCount() - 1)).length);
    if (stepDigits !== this.lastStepDigits) {
      this.cachedStepWidthPx = 0;
      this.lastStepDigits = stepDigits;
      this.table.style.setProperty("--step-digits", String(stepDigits));
    }

    // Resolve widths before window math so pads stay integer-aligned.
    void this.trackWidthPx();
    void this.stepWidthPx();

    const { start, end } = this.computeVisibleRange();
    const cols = this.computeVisibleTrackRange();
    const rowsUnchanged = !force && start === this.vStart && end === this.vEnd;
    const colsUnchanged = !force && cols.start === this.cStart && cols.end === this.cEnd;
    if (rowsUnchanged && colsUnchanged) {
      this.paintPlayhead();
      return;
    }

    const need = Math.max(0, end - start);
    const needCols = Math.max(0, cols.end - cols.start);
    const prevStart = this.vStart;
    const prevEnd = this.vEnd;
    const prevNeed = Math.max(0, prevEnd - prevStart);
    const delta = start - prevStart;
    const prevScroll = this.wrap.scrollTop;
    const prevScrollLeft = this.wrap.scrollLeft;
    const colsChanged = !colsUnchanged;
    this.beginScrollSuppress();
    // Lock scrollWidth before adding/hiding columns (prevents thumb resize).
    this.syncTableScrollWidth();
    // Bind uses cStart/cEnd — publish the new window before rebinding glyphs.
    this.cStart = cols.start;
    this.cEnd = cols.end;
    this.ensurePool(need, needCols);
    // Pads before column show/hide so left+cols+right stays tracks*trackW.
    this.updateColumnPads(cols.start, cols.end);
    this.ensureColumnSlots(needCols);
    this.updatePadHeights(start, end);
    this.syncTableScrollWidth();

    const canSlide =
      !force
      && !colsChanged
      && need > 0
      && prevNeed === need
      && delta !== 0
      && Math.abs(delta) < need
      && this.pool.length >= need
      && this.pool[0]!.step === prevStart;

    if (!rowsUnchanged) {
      if (canSlide) {
        this.slidePool(delta, start, need, stepDigits);
      } else {
        for (let i = 0; i < need; i++) {
          this.bindPooledRow(this.pool[i]!, start + i, stepDigits);
        }
        const tbody = this.table.tBodies[0];
        const bottom = this.bottomPad;
        if (tbody && bottom) {
          for (let i = 0; i < need; i++) {
            tbody.insertBefore(this.pool[i]!.tr, bottom);
          }
        }
      }
    } else if (colsChanged) {
      for (let i = 0; i < need; i++) {
        this.bindRowColumns(this.pool[i]!);
      }
    }

    this.vStart = start;
    this.vEnd = end;
    this.rowByStep.clear();
    for (let i = 0; i < need; i++) {
      this.rowByStep.set(start + i, this.pool[i]!);
    }
    for (let i = need; i < this.pool.length; i++) {
      this.pool[i]!.tr.remove();
    }

    if (colsChanged) {
      this.syncHeaderColumns();
    }

    // Restore after pad/header mutations — scrollWidth must already be stable.
    if (this.wrap.scrollTop !== prevScroll) {
      this.wrap.scrollTop = prevScroll;
    }
    if (this.wrap.scrollLeft !== prevScrollLeft) {
      this.wrap.scrollLeft = prevScrollLeft;
    }
    this.endScrollSuppress();

    this.paintCursor({ selectionUi: false });
    this.paintPlayhead();
  }

  private updatePadHeights(start: number, end: number): void {
    const topPx = start * TRACKER_ROW_HEIGHT_PX;
    const bottomPx = Math.max(0, this.stepCount() - end) * TRACKER_ROW_HEIGHT_PX;
    this.applyPadHeight(this.topPad, this.topPadInner, topPx);
    this.applyPadHeight(this.bottomPad, this.bottomPadInner, bottomPx);
  }

  /** Set spacer height on both the inner div and the cell — tables sometimes ignore div-only height. */
  private applyPadHeight(
    tr: HTMLTableRowElement | undefined,
    inner: HTMLDivElement | undefined,
    heightPx: number,
  ): void {
    const px = `${Math.max(0, heightPx)}px`;
    if (inner) {
      inner.style.height = px;
    }
    const td = tr?.cells[0];
    if (td) {
      td.style.height = px;
      td.style.minHeight = px;
    }
  }

  private updateColumnPads(start: number, end: number): void {
    const trackW = this.trackWidthPx();
    const leftPx = start * trackW;
    const rightPx = Math.max(0, this.slotCount() - end) * trackW;
    for (const row of this.pool) {
      this.applyPadWidth(row.leftPad, row.leftPadInner, leftPx);
      this.applyPadWidth(row.rightPad, row.rightPadInner, rightPx);
    }
  }

  private applyPadWidth(
    td: HTMLTableCellElement,
    inner: HTMLDivElement,
    widthPx: number,
  ): void {
    const px = `${Math.max(0, Math.round(widthPx))}px`;
    td.style.width = px;
    td.style.minWidth = px;
    td.style.maxWidth = px;
    inner.style.width = px;
  }

  /**
   * Imperative virtualized track header — update pads + heads in place so
   * scrollWidth never collapses mid-H-scroll (that resized the scrollbar thumb).
   */
  private syncHeaderColumns(): void {
    const thead = this.table.tHead;
    const row = (thead?.querySelector("tr[data-tracker-header]")
      ?? thead?.rows[0]) as HTMLTableRowElement | null;
    if (!row) {
      return;
    }

    const stepHead = row.querySelector("th.step-head") as HTMLTableCellElement | null;
    if (!stepHead) {
      return;
    }

    const trackW = this.trackWidthPx();
    const leftPx = this.cStart * trackW;
    const rightPx = Math.max(0, this.slotCount() - this.cEnd) * trackW;
    const needCols = Math.max(0, this.cEnd - this.cStart);
    const anySolo = this.solo.some(Boolean);
    const names = this.getTrackNames();

    let leftPad = row.querySelector("th.track-pad[data-pad='left']") as HTMLTableCellElement | null;
    let rightPad = row.querySelector("th.track-pad[data-pad='right']") as HTMLTableCellElement | null;
    if (!leftPad) {
      leftPad = this.createHeaderPad(leftPx, "left");
      stepHead.after(leftPad);
    } else {
      this.applyHeaderPadWidth(leftPad, leftPx);
    }
    if (!rightPad) {
      rightPad = this.createHeaderPad(rightPx, "right");
      row.append(rightPad);
    } else {
      this.applyHeaderPadWidth(rightPad, rightPx);
    }

    const existing = [...row.querySelectorAll("th.track-head")] as HTMLTableCellElement[];
    while (existing.length < needCols) {
      const track = this.cStart + existing.length;
      const th = this.createHeaderTrack(track, names[track] ?? `I${track + 1}`, anySolo);
      rightPad.before(th);
      existing.push(th);
    }
    while (existing.length > needCols) {
      existing.pop()?.remove();
    }

    for (let i = 0; i < needCols; i++) {
      const track = this.cStart + i;
      this.bindHeaderTrack(
        existing[i]!,
        track,
        names[track] ?? `I${track + 1}`,
        anySolo,
      );
      this.applyFixedColWidth(existing[i]!, trackW);
    }

    // Keep DOM order: step | left | heads… | right
    let anchor: ChildNode = leftPad;
    for (const th of existing.slice(0, needCols)) {
      if (th.previousSibling !== anchor) {
        anchor.after(th);
      }
      anchor = th;
    }
    if (rightPad.previousSibling !== anchor) {
      anchor.after(rightPad);
    }
  }

  private applyHeaderPadWidth(th: HTMLTableCellElement, widthPx: number): void {
    const px = `${Math.max(0, Math.round(widthPx))}px`;
    th.style.width = px;
    th.style.minWidth = px;
    th.style.maxWidth = px;
    const inner = th.querySelector(".track-pad-inner") as HTMLDivElement | null;
    if (inner) {
      inner.style.width = px;
    }
  }

  private createHeaderPad(widthPx: number, side: "left" | "right"): HTMLTableCellElement {
    const th = document.createElement("th");
    th.className = "track-pad";
    th.dataset.pad = side;
    th.setAttribute("aria-hidden", "true");
    const inner = document.createElement("div");
    inner.className = "track-pad-inner";
    th.append(inner);
    this.applyHeaderPadWidth(th, widthPx);
    return th;
  }

  private bindHeaderTrack(
    th: HTMLTableCellElement,
    track: number,
    name: string,
    anySolo: boolean,
  ): void {
    const muted = this.muted[track] === true;
    const solo = this.solo[track] === true;
    const audible = anySolo ? solo : !muted;
    th.className = `track-head${muted && !anySolo ? " is-muted" : ""}${solo ? " is-solo" : ""}${!audible ? " is-dimmed" : ""}`;
    th.dataset.track = String(track);
    th.title = `${name} — drag to reorder · double-click to rename · right-click for name & color`;
    this.applyTrackTint(th, track);
    th.draggable = true;

    const top = th.querySelector(".track-head-top") as HTMLDivElement | null;
    if (!top) {
      return;
    }
    const nameEl = top.querySelector(".track-head-name") as HTMLElement | null;
    if (nameEl && !th.querySelector(".track-head-input")) {
      nameEl.textContent = name;
      nameEl.title = name;
    }
    const soloBtn = top.querySelector(".track-solo") as HTMLButtonElement | null;
    const muteBtn = top.querySelector(".track-mute") as HTMLButtonElement | null;
    if (soloBtn) {
      soloBtn.classList.toggle("active", solo);
      soloBtn.setAttribute("aria-pressed", solo ? "true" : "false");
      soloBtn.setAttribute("aria-label", `Solo instrument ${track + 1}`);
      soloBtn.onclick = event => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleSolo(track);
      };
    }
    if (muteBtn) {
      muteBtn.classList.toggle("active", muted);
      muteBtn.setAttribute("aria-pressed", muted ? "true" : "false");
      muteBtn.setAttribute("aria-label", `Mute instrument ${track + 1}`);
      muteBtn.onclick = event => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleMute(track);
      };
    }
    th.ondblclick = event => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.(".track-head-controls, .track-head-input")) {
        return;
      }
      event.preventDefault();
      this.beginHeaderRename(th, track, name);
    };
    this.bindHeaderTrackDrag(th, track);
  }

  private bindHeaderTrackDrag(th: HTMLTableCellElement, track: number): void {
    th.ondragstart = event => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.(".track-head-controls, .track-head-input")) {
        event.preventDefault();
        return;
      }
      this.headerDragFrom = track;
      event.dataTransfer?.setData("application/x-adam-track", String(track));
      event.dataTransfer?.setData("text/plain", `track:${track}`);
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
      }
      th.classList.add("is-dragging");
    };
    th.ondragover = event => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
      if (this.headerDragFrom != null && this.headerDragFrom !== track) {
        th.classList.add("is-drop-target");
      }
    };
    th.ondragleave = () => {
      th.classList.remove("is-drop-target");
    };
    th.ondrop = event => {
      event.preventDefault();
      th.classList.remove("is-drop-target");
      const raw =
        event.dataTransfer?.getData("application/x-adam-track")
        || event.dataTransfer?.getData("text/plain")?.replace(/^track:/, "")
        || "";
      const from = this.headerDragFrom ?? Number(raw);
      this.headerDragFrom = null;
      th.classList.remove("is-dragging");
      if (!Number.isFinite(from) || from === track) {
        return;
      }
      getLabActions()?.reorderTracks(from, track);
    };
    th.ondragend = () => {
      this.headerDragFrom = null;
      th.classList.remove("is-dragging");
      for (const el of this.table.querySelectorAll("th.track-head.is-drop-target")) {
        el.classList.remove("is-drop-target");
      }
      for (const el of this.table.querySelectorAll("th.track-head.is-dragging")) {
        el.classList.remove("is-dragging");
      }
    };
  }

  private createHeaderTrack(track: number, name: string, anySolo: boolean): HTMLTableCellElement {
    const muted = this.muted[track] === true;
    const solo = this.solo[track] === true;
    const audible = anySolo ? solo : !muted;
    const th = document.createElement("th");
    th.className = `track-head${muted && !anySolo ? " is-muted" : ""}${solo ? " is-solo" : ""}${!audible ? " is-dimmed" : ""}`;
    th.dataset.track = String(track);
    th.title = `${name} — drag to reorder · double-click to rename · right-click for name & color`;
    this.applyTrackTint(th, track);

    const top = document.createElement("div");
    top.className = "track-head-top";
    const nameEl = document.createElement("span");
    nameEl.className = "track-head-name";
    nameEl.textContent = name;
    nameEl.title = name;
    const controls = document.createElement("div");
    controls.className = "track-head-controls";

    const soloBtn = document.createElement("button");
    soloBtn.type = "button";
    soloBtn.className = `track-ms track-solo${solo ? " active" : ""}`;
    soloBtn.title = "Solo";
    soloBtn.setAttribute("aria-label", `Solo instrument ${track + 1}`);
    soloBtn.setAttribute("aria-pressed", solo ? "true" : "false");
    soloBtn.textContent = "S";
    soloBtn.addEventListener("mousedown", event => event.stopPropagation());

    const muteBtn = document.createElement("button");
    muteBtn.type = "button";
    muteBtn.className = `track-ms track-mute${muted ? " active" : ""}`;
    muteBtn.title = "Mute";
    muteBtn.setAttribute("aria-label", `Mute instrument ${track + 1}`);
    muteBtn.setAttribute("aria-pressed", muted ? "true" : "false");
    muteBtn.textContent = "M";
    muteBtn.addEventListener("mousedown", event => event.stopPropagation());

    controls.append(soloBtn, muteBtn);
    top.append(nameEl, controls);
    th.append(top);
    this.bindHeaderTrack(th, track, name, anySolo);
    return th;
  }

  private beginHeaderRename(th: HTMLTableCellElement, track: number, current: string): void {
    if (th.querySelector(".track-head-input")) {
      return;
    }
    const top = th.querySelector(".track-head-top");
    const nameEl = th.querySelector(".track-head-name");
    if (!top || !nameEl) {
      return;
    }
    const input = document.createElement("input");
    input.type = "text";
    input.className = "track-head-input";
    input.value = current;
    input.maxLength = 40;
    input.setAttribute("aria-label", `Rename instrument ${track + 1}`);
    nameEl.replaceWith(input);
    input.focus();
    input.select();

    let finished = false;
    const finish = (commit: boolean): void => {
      if (finished) {
        return;
      }
      finished = true;
      const value = input.value;
      // Put the name span back before sync — bindHeaderTrack skips heads that
      // still have a .track-head-input, so Enter/Escape used to leave the field open.
      const restored = document.createElement("span");
      restored.className = "track-head-name";
      restored.textContent = current;
      restored.title = current;
      input.replaceWith(restored);
      if (commit) {
        this.commitTrackName(track, value);
      } else {
        this.refreshHeader();
      }
      this.focus();
    };
    input.addEventListener("blur", () => finish(true));
    input.addEventListener("keydown", event => {
      event.stopPropagation();
      if (event.key === "Enter") {
        event.preventDefault();
        finish(true);
      } else if (event.key === "Escape") {
        event.preventDefault();
        finish(false);
      }
    });
  }

  private ensureColumnSlots(needCols: number): void {
    for (const row of this.pool) {
      while (row.slots.length < needCols) {
        row.slots.push(this.createTrackSlot(row));
      }
      for (let i = 0; i < row.slots.length; i++) {
        row.slots[i]!.td.hidden = i >= needCols;
      }
    }
  }

  private createTrackSlot(row: PooledRow): TrackSlot {
    const td = document.createElement("td");
    td.className = "track-cell";
    td.dataset.track = "-1";
    const note = document.createElement("span");
    note.className = "field note";
    const vol = document.createElement("span");
    vol.className = "field vol";
    const fx = document.createElement("span");
    fx.className = "field fx";
    const chars: HTMLElement[] = [];
    for (let offset = 0; offset < CELL_WIDTH; offset++) {
      const ch = document.createElement("span");
      ch.className = "ch";
      ch.dataset.track = "-1";
      ch.dataset.offset = String(offset);
      ch.dataset.field = fieldForOffset(offset);
      setGlyph(ch, ".");
      chars.push(ch);
      if (offset < NOTE_WIDTH) {
        note.append(ch);
      } else if (offset < NOTE_WIDTH + VOL_WIDTH) {
        vol.append(ch);
      } else {
        fx.append(ch);
      }
    }
    td.append(note, vol, fx);
    this.applyFixedColWidth(td, this.trackWidthPx());
    row.tr.insertBefore(td, row.rightPad);
    return { td, chars, track: -1 };
  }

  private charsForTrack(row: PooledRow, track: number): HTMLElement[] | undefined {
    const index = track - this.cStart;
    if (index < 0 || index >= this.cEnd - this.cStart) {
      return undefined;
    }
    return row.slots[index]?.chars;
  }

  /**
   * Scroll follow: recycle leaving rows onto the entering edge instead of
   * rewriting every visible glyph (critical for wide / long patterns).
   */
  private slidePool(delta: number, start: number, need: number, stepDigits: number): void {
    const tbody = this.table.tBodies[0];
    const bottom = this.bottomPad;
    if (delta > 0) {
      const moved = this.pool.splice(0, delta);
      this.pool.splice(need - delta, 0, ...moved);
      for (let i = 0; i < delta; i++) {
        const index = need - delta + i;
        this.bindPooledRow(this.pool[index]!, start + index, stepDigits);
      }
      // Only the recycled rows move in the DOM (to just above bottom pad).
      if (tbody && bottom) {
        for (let i = need - delta; i < need; i++) {
          tbody.insertBefore(this.pool[i]!.tr, bottom);
        }
      }
    } else {
      const count = -delta;
      const moved = this.pool.splice(need - count, count);
      this.pool.splice(0, 0, ...moved);
      for (let i = 0; i < count; i++) {
        this.bindPooledRow(this.pool[i]!, start + i, stepDigits);
      }
      // Insert recycled rows after the top pad / before the first kept row.
      if (tbody) {
        const anchor = this.pool[count]?.tr ?? bottom;
        if (anchor) {
          for (let i = 0; i < count; i++) {
            tbody.insertBefore(this.pool[i]!.tr, anchor);
          }
        }
      }
    }
  }

  /**
   * React may wipe imperative `<tbody>` children on chrome re-renders.
   * Call after React commit to rebuild the row pool if pads were detached.
   */
  recoverPoolDom(): void {
    const tbody = this.table.tBodies[0];
    const padsAttached =
      !!this.topPad
      && !!this.bottomPad
      && !!tbody
      && this.topPad.parentNode === tbody
      && this.bottomPad.parentNode === tbody;
    if (padsAttached) {
      // React may have replaced thead track cells while leaving tbody alone.
      this.syncHeaderColumns();
      return;
    }
    this.vStart = -1;
    this.vEnd = -1;
    this.cStart = -1;
    this.cEnd = -1;
    this.syncWindow(true);
  }

  /** Fill a vertical hex selection by lerping first→last row endpoints. */
  applySelectionRamp(): boolean {
    const analysis = this.analyzeCurrentSelectionTools();
    if (!analysis?.ok || !analysis.canRamp || analysis.start === undefined || analysis.end === undefined) {
      return false;
    }
    const { bounds, start, end, width } = analysis;
    const rowCount = bounds.maxStep - bounds.minStep + 1;
    const values = rampHexValues(start, end, rowCount, width);
    this.startRecord("edit");
    for (let i = 0; i < values.length; i++) {
      const step = bounds.minStep + i;
      const hex = values[i]!;
      this.writeSelectionRowGlyphs(step, bounds.minFlat, hex);
    }
    this.finishRecord("Selection ramp");
    this.paintCursor();
    return true;
  }

  /** Stamp the top-row selection glyphs onto every row in the selection. */
  applySelectionFillTop(): boolean {
    const analysis = this.analyzeCurrentSelectionTools();
    if (!analysis?.ok || !analysis.canFillTop) {
      return false;
    }
    const { bounds, startGlyphs } = analysis;
    this.startRecord("edit");
    for (let step = bounds.minStep; step <= bounds.maxStep; step++) {
      this.writeSelectionRowGlyphs(step, bounds.minFlat, startGlyphs);
    }
    this.finishRecord("Fill from top");
    this.paintCursor();
    return true;
  }

  /** Fill every selected row with a fresh random hex value (selection width). */
  applySelectionRandomHex(): boolean {
    const analysis = this.analyzeCurrentSelectionTools();
    if (!analysis?.ok) {
      return false;
    }
    const { bounds, width } = analysis;
    const rowCount = bounds.maxStep - bounds.minStep + 1;
    const values = randomHexValues(rowCount, width);
    this.startRecord("edit");
    for (let i = 0; i < values.length; i++) {
      this.writeSelectionRowGlyphs(bounds.minStep + i, bounds.minFlat, values[i]!);
    }
    this.finishRecord("Random hex");
    this.paintCursor();
    return true;
  }

  /**
   * Target for Generate Notes: selection rows on the leftmost selected track,
   * or the cursor cell when there is no selection.
   */
  getGenerateNotesTarget(): { minStep: number; maxStep: number; track: number; rowCount: number } {
    const bounds = this.selectionBounds();
    if (bounds) {
      const track = Math.floor(bounds.minFlat / CELL_WIDTH);
      const rowCount = bounds.maxStep - bounds.minStep + 1;
      return {
        minStep: bounds.minStep,
        maxStep: bounds.maxStep,
        track,
        rowCount,
      };
    }
    return {
      minStep: this.cursor.step,
      maxStep: this.cursor.step,
      track: this.cursor.track,
      rowCount: 1,
    };
  }

  /**
   * Write generated MIDI notes into the selection/cursor rows on one track.
   * `null` clears the note field (gap rows).
   */
  applySelectionGeneratedNotes(rowNotes: Array<number | null>): boolean {
    if (rowNotes.length === 0) {
      return false;
    }
    const target = this.getGenerateNotesTarget();
    this.startRecord("edit");
    for (let i = 0; i < rowNotes.length; i++) {
      const step = target.minStep + i;
      if (step > target.maxStep || step >= this.stepCount()) {
        break;
      }
      const midi = rowNotes[i];
      if (midi == null) {
        this.writeNoteField(step, target.track, "", { silent: true });
      } else {
        this.writeNoteField(step, target.track, formatNoteName(midi), { silent: true });
      }
    }
    this.finishRecord("Generate notes");
    this.paintCursor();
    return true;
  }

  private writeSelectionRowGlyphs(step: number, minFlat: number, glyphs: string): void {
    for (let d = 0; d < glyphs.length; d++) {
      const flat = minFlat + d;
      const track = Math.floor(flat / CELL_WIDTH) as InstrumentSlot;
      const offset = flat % CELL_WIDTH;
      this.writeGlyph(step, track, offset, glyphs[d]!, { silent: true });
    }
  }

  private analyzeCurrentSelectionTools() {
    const bounds = this.selectionBounds();
    if (!bounds || !this.selectionIsRange()) {
      return undefined;
    }
    return analyzeSelectionTools(bounds, step => this.readSelectionRowGlyphs(step, bounds));
  }

  private readSelectionRowGlyphs(
    step: number,
    bounds: { minFlat: number; maxFlat: number },
  ): string {
    let out = "";
    for (let flat = bounds.minFlat; flat <= bounds.maxFlat; flat++) {
      const track = Math.floor(flat / CELL_WIDTH);
      const offset = flat % CELL_WIDTH;
      out += this.bufferAt(step, track)[offset] ?? ".";
    }
    return out;
  }

  private cursorScreenRect():
    | { left: number; top: number; right: number; bottom: number }
    | undefined {
    const ch = this.cursorCh;
    if (!ch) {
      return undefined;
    }
    const r = ch.getBoundingClientRect();
    if (!Number.isFinite(r.left) || r.width <= 0) {
      return undefined;
    }
    return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
  }

  private notifySelectionUi(): void {
    const analysis = this.analyzeCurrentSelectionTools();
    if (!analysis?.ok) {
      this.options.onSelectionUi?.(null);
      return;
    }
    const anchor = this.cursorScreenRect();
    if (!anchor) {
      this.options.onSelectionUi?.(null);
      return;
    }
    this.options.onSelectionUi?.({
      anchor,
      canRamp: analysis.canRamp,
      canFillTop: analysis.canFillTop,
    });
  }

  private ensurePool(need: number, needCols: number): void {
    const tbody = this.table.tBodies[0] ?? this.table.createTBody();
    // step + left pad + visible tracks + right pad
    const colSpan = Math.max(1, 3 + needCols);

    // React owns `<tbody />` in JSX and clears unknown children on re-render
    // (header / selection UI setState). Pads then become detached → insertBefore throws.
    const padsAttached =
      !!this.topPad
      && !!this.bottomPad
      && this.topPad.parentNode === tbody
      && this.bottomPad.parentNode === tbody;

    if (!padsAttached) {
      tbody.replaceChildren();
      this.pool.length = 0;
      this.rowByStep.clear();
      this.clearPaintRefs();
      const top = this.makePadRow(colSpan, 0);
      const bottom = this.makePadRow(colSpan, 0);
      this.topPad = top.tr;
      this.bottomPad = bottom.tr;
      this.topPadInner = top.inner;
      this.bottomPadInner = bottom.inner;
      tbody.append(this.topPad, this.bottomPad);
    } else if (this.topPad && this.bottomPad) {
      const topTd = this.topPad.cells[0];
      const bottomTd = this.bottomPad.cells[0];
      if (topTd && topTd.colSpan !== colSpan) {
        topTd.colSpan = colSpan;
      }
      if (bottomTd && bottomTd.colSpan !== colSpan) {
        bottomTd.colSpan = colSpan;
      }
    }

    while (this.pool.length < need) {
      const row = this.createPooledRow();
      this.pool.push(row);
    }
    const bottom = this.bottomPad;
    if (!bottom || bottom.parentNode !== tbody) {
      return;
    }
    for (let i = 0; i < need; i++) {
      const tr = this.pool[i]!.tr;
      if (tr.parentNode !== tbody || tr.nextSibling !== bottom) {
        tbody.insertBefore(tr, bottom);
      }
    }
  }

  private makePadRow(
    colSpan: number,
    heightPx: number,
  ): { tr: HTMLTableRowElement; inner: HTMLDivElement } {
    const tr = document.createElement("tr");
    tr.className = "vs-pad";
    tr.setAttribute("aria-hidden", "true");
    const td = document.createElement("td");
    td.colSpan = colSpan;
    const px = `${Math.max(0, heightPx)}px`;
    td.style.height = px;
    td.style.minHeight = px;
    const inner = document.createElement("div");
    inner.className = "vs-pad-inner";
    inner.style.height = px;
    td.append(inner);
    tr.append(td);
    return { tr, inner };
  }

  private createPadCell(): { td: HTMLTableCellElement; inner: HTMLDivElement } {
    const td = document.createElement("td");
    td.className = "track-pad";
    td.setAttribute("aria-hidden", "true");
    const inner = document.createElement("div");
    inner.className = "track-pad-inner";
    td.append(inner);
    return { td, inner };
  }

  private createPooledRow(): PooledRow {
    const tr = document.createElement("tr");
    tr.className = "tracker-row";
    const stepCell = tr.insertCell();
    stepCell.className = "step";
    const left = this.createPadCell();
    const right = this.createPadCell();
    tr.append(left.td, right.td);
    return {
      tr,
      stepCell,
      leftPad: left.td,
      leftPadInner: left.inner,
      rightPad: right.td,
      rightPadInner: right.inner,
      slots: [],
      step: -1,
    };
  }

  private bindPooledRow(row: PooledRow, step: number, stepDigits: number): void {
    row.step = step;
    row.tr.hidden = false;
    row.tr.dataset.step = String(step);
    row.tr.classList.remove("cursor-row", "playhead", "bar", "beat", "marked", "pattern-dim");
    const ref = locateViewStep(this.clip.patterns, this.viewPatternIds, step);
    const local = ref?.localStep ?? step;
    const pattern = ref ? findPattern(this.clip, ref.patternId) : this.pattern();
    const bar = this.stepsPerBar();
    if (local % bar === 0) {
      row.tr.classList.add("bar");
    } else if (local % 4 === 0) {
      row.tr.classList.add("beat");
    }
    if (ref && ref.patternId !== this.focusPatternId()) {
      row.tr.classList.add("pattern-dim");
    }
    row.stepCell.textContent = String(local).padStart(stepDigits, "0");
    const marker = getRowMarker(pattern, local);
    if (marker) {
      row.tr.classList.add("marked");
      row.tr.style.setProperty("--mark", rowMarkerColorHex(marker.color));
      row.stepCell.classList.add("has-label");
      row.stepCell.dataset.label = marker.label;
    } else {
      row.tr.style.removeProperty("--mark");
      row.stepCell.classList.remove("has-label");
      delete row.stepCell.dataset.label;
    }
    this.bindRowColumns(row);
  }

  /** Rebind recycled track slots to the current [cStart, cEnd) window. */
  private bindRowColumns(row: PooledRow): void {
    const needCols = Math.max(0, this.cEnd - this.cStart);
    for (let i = 0; i < needCols; i++) {
      const track = this.cStart + i;
      const slot = row.slots[i];
      if (!slot) {
        continue;
      }
      slot.track = track;
      slot.td.hidden = false;
      slot.td.dataset.track = String(track);
      {
        const ref = locateViewStep(this.clip.patterns, this.viewPatternIds, row.step);
        const pattern = ref ? findPattern(this.clip, ref.patternId) : this.pattern();
        const patternMuted = pattern?.tracks[track]?.muted === true;
        slot.td.classList.toggle("is-dimmed", !this.isTrackAudible(track, patternMuted));
      }
      const buffer = this.bufferAt(row.step, track);
      for (let offset = 0; offset < CELL_WIDTH; offset++) {
        const ch = slot.chars[offset]!;
        ch.dataset.track = String(track);
        ch.dataset.step = String(row.step);
        ch.classList.remove("cursor", "selected");
        setGlyph(ch, buffer[offset] ?? ".");
      }
    }
    for (let i = needCols; i < row.slots.length; i++) {
      const slot = row.slots[i]!;
      slot.track = -1;
      slot.td.hidden = true;
    }
  }

  private paintCursor(options?: { selectionUi?: boolean }): void {
    if (this.cursorCh) {
      this.cursorCh.classList.remove("cursor");
      this.cursorCh = undefined;
    }
    if (this.cursorRowEl) {
      this.cursorRowEl.classList.remove("cursor-row");
      this.cursorRowEl = undefined;
    }
    for (const ch of this.selectedChs) {
      ch.classList.remove("selected");
    }
    this.selectedChs.length = 0;

    const { step, track, offset } = this.cursor;
    const row = this.rowByStep.get(step);
    if (row) {
      row.tr.classList.add("cursor-row");
      this.cursorRowEl = row.tr;
      const ch = this.charsForTrack(row, track)?.[offset];
      if (ch) {
        ch.classList.add("cursor");
        this.cursorCh = ch;
      }
    }

    const bounds = this.selectionBounds();
    if (!bounds) {
      this.notifyPositionChange();
      if (options?.selectionUi !== false) {
        this.notifySelectionUi();
      }
      return;
    }
    for (let s = bounds.minStep; s <= bounds.maxStep; s++) {
      const pooled = this.rowByStep.get(s);
      if (!pooled) {
        continue;
      }
      for (let flat = bounds.minFlat; flat <= bounds.maxFlat; flat++) {
        const t = Math.floor(flat / CELL_WIDTH);
        const o = flat % CELL_WIDTH;
        const ch = this.charsForTrack(pooled, t)?.[o];
        if (ch) {
          ch.classList.add("selected");
          this.selectedChs.push(ch);
        }
      }
    }
    this.notifyPositionChange();
    if (options?.selectionUi !== false) {
      this.notifySelectionUi();
    }
  }

  private notifyPositionChange(): void {
    if (
      this.cursor.step === this.lastNotifiedStep
      && this.cursor.track === this.lastNotifiedTrack
      && this.playheadRow === this.lastNotifiedPlayhead
    ) {
      return;
    }
    this.lastNotifiedStep = this.cursor.step;
    this.lastNotifiedTrack = this.cursor.track;
    this.lastNotifiedPlayhead = this.playheadRow;
    this.options.onPositionChange?.();
  }

  private flatOf(cursor: TrackerCursor): number {
    return flatOfPure(cursor.track, cursor.offset);
  }

  private cursorFromFlat(step: number, flat: number): TrackerCursor {
    const maxFlat = this.slotCount() * CELL_WIDTH - 1;
    const f = Math.min(maxFlat, Math.max(0, flat));
    const { track, offset } = cursorFromFlatPure(f);
    return {
      step: Math.min(this.stepCount() - 1, Math.max(0, step)),
      track: track as InstrumentSlot,
      offset,
    };
  }

  private posFromCh(ch: HTMLElement): { step: number; flat: number } {
    const track = Number(ch.dataset.track);
    const offset = Number(ch.dataset.offset);
    return {
      step: Number(ch.dataset.step),
      flat: track * CELL_WIDTH + offset,
    };
  }

  /**
   * Map a pointer to a tracker glyph, including clicks in cell padding / field
   * margins (gaps that are not themselves `.ch` nodes).
   */
  private posFromClientPoint(
    clientX: number,
    clientY: number,
  ): { step: number; flat: number } | undefined {
    const hit = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    if (hit?.closest?.("th.track-head, .track-head-input, thead, td.step")) {
      return undefined;
    }

    const directCh = hit?.closest?.(".ch") as HTMLElement | null;
    if (directCh && this.table.contains(directCh)) {
      return this.posFromCh(directCh);
    }

    const td = hit?.closest?.("td.track-cell") as HTMLTableCellElement | null;
    if (td && this.table.contains(td)) {
      return this.nearestCharPosInTrackCell(td, clientX, clientY);
    }

    const tr = hit?.closest?.("tr.tracker-row") as HTMLTableRowElement | null;
    if (tr && this.table.contains(tr)) {
      return this.nearestCharPosInRow(tr, clientX, clientY);
    }

    return this.posFromGeometry(clientX, clientY);
  }

  private nearestCharPosInTrackCell(
    td: HTMLTableCellElement,
    clientX: number,
    clientY: number,
  ): { step: number; flat: number } | undefined {
    const chars = td.querySelectorAll(".ch");
    if (chars.length === 0) {
      return undefined;
    }
    let best: HTMLElement | undefined;
    let bestDist = Infinity;
    for (const node of chars) {
      const ch = node as HTMLElement;
      const rect = ch.getBoundingClientRect();
      const cx = (rect.left + rect.right) / 2;
      const cy = (rect.top + rect.bottom) / 2;
      const dist = (clientX - cx) ** 2 + (clientY - cy) ** 2;
      if (dist < bestDist) {
        bestDist = dist;
        best = ch;
      }
    }
    return best ? this.posFromCh(best) : undefined;
  }

  private nearestCharPosInRow(
    tr: HTMLTableRowElement,
    clientX: number,
    clientY: number,
  ): { step: number; flat: number } | undefined {
    const cells = tr.querySelectorAll("td.track-cell");
    let bestTd: HTMLTableCellElement | undefined;
    let bestDist = Infinity;
    for (const node of cells) {
      const td = node as HTMLTableCellElement;
      const rect = td.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) {
        return this.nearestCharPosInTrackCell(td, clientX, clientY);
      }
      const cx = (rect.left + rect.right) / 2;
      const dist = Math.abs(clientX - cx);
      if (dist < bestDist) {
        bestDist = dist;
        bestTd = td;
      }
    }
    return bestTd
      ? this.nearestCharPosInTrackCell(bestTd, clientX, clientY)
      : undefined;
  }

  /** Fallback when the event target is the wrap / pad / empty table chrome. */
  private posFromGeometry(
    clientX: number,
    clientY: number,
  ): { step: number; flat: number } | undefined {
    let bestRow: PooledRow | undefined;
    let bestRowDist = Infinity;
    for (const row of this.pool) {
      if (row.step < 0 || row.tr.hidden) {
        continue;
      }
      const rect = row.tr.getBoundingClientRect();
      if (clientY >= rect.top && clientY <= rect.bottom) {
        bestRow = row;
        break;
      }
      const cy = (rect.top + rect.bottom) / 2;
      const dist = Math.abs(clientY - cy);
      if (dist < bestRowDist) {
        bestRowDist = dist;
        bestRow = row;
      }
    }
    if (!bestRow) {
      return undefined;
    }
    const fromDom = this.nearestCharPosInRow(bestRow.tr, clientX, clientY);
    if (fromDom) {
      return fromDom;
    }
    // Click landed on a horizontal pad / gap — map via scroll geometry.
    const wrapRect = this.wrap.getBoundingClientRect();
    const x = clientX - wrapRect.left + this.wrap.scrollLeft;
    const trackW = this.trackWidthPx();
    const stepW = this.stepWidthPx();
    const track = Math.max(
      0,
      Math.min(this.slotCount() - 1, Math.floor((x - stepW) / trackW)),
    );
    const offset = Math.max(
      0,
      Math.min(CELL_WIDTH - 1, Math.floor(((x - stepW) % trackW) / (trackW / CELL_WIDTH))),
    );
    return { step: bestRow.step, flat: track * CELL_WIDTH + offset };
  }

  private selectionBounds():
    | { minStep: number; maxStep: number; minFlat: number; maxFlat: number }
    | undefined {
    if (!this.selAnchor || !this.selFocus) {
      return undefined;
    }
    return {
      minStep: Math.min(this.selAnchor.step, this.selFocus.step),
      maxStep: Math.max(this.selAnchor.step, this.selFocus.step),
      minFlat: Math.min(this.selAnchor.flat, this.selFocus.flat),
      maxFlat: Math.max(this.selAnchor.flat, this.selFocus.flat),
    };
  }

  private selectionIsRange(): boolean {
    const b = this.selectionBounds();
    if (!b) {
      return false;
    }
    return b.minStep !== b.maxStep || b.minFlat !== b.maxFlat;
  }

  private clearSelection(): void {
    this.selAnchor = undefined;
    this.selFocus = undefined;
    this.paintCursor();
  }

  private setSelection(anchor: { step: number; flat: number }, focus: { step: number; flat: number }): void {
    this.selAnchor = anchor;
    this.selFocus = focus;
    this.cursor = this.cursorFromFlat(focus.step, focus.flat);
    this.paintCursor();
  }

  private onMouseDown(event: MouseEvent): void {
    if ((event.target as HTMLElement | null)?.closest?.("[data-selection-toolbar]")) {
      // Toolbar is inside the focus host; don't treat clicks as grid hits.
      return;
    }
    if ((event.target as HTMLElement | null)?.closest?.("th.track-head, .track-head-input")) {
      return;
    }

    const stepCell = (event.target as HTMLElement | null)?.closest?.("td.step") as
      | HTMLTableCellElement
      | null;
    if (stepCell && this.table.contains(stepCell)) {
      event.preventDefault();
      event.stopPropagation();
      const step = Number(stepCell.closest("tr")?.dataset.step);
      if (Number.isInteger(step)) {
        this.openRowLabelEditor(step, stepCell.getBoundingClientRect());
      }
      this.focusHost.focus();
      return;
    }

    const pos = this.posFromClientPoint(event.clientX, event.clientY);
    if (!pos) {
      this.focusHost.focus();
      return;
    }
    event.preventDefault();
    this.endTypingCoalesce();
    this.cursor = this.cursorFromFlat(pos.step, pos.flat);
    this.syncActiveFromCursor();
    if (event.shiftKey && this.selAnchor) {
      this.selFocus = pos;
    } else {
      this.selAnchor = pos;
      this.selFocus = pos;
      this.dragging = true;
      this.dragPointer = { x: event.clientX, y: event.clientY };
      window.addEventListener("mousemove", this.onWindowMouseMove);
      window.addEventListener("mouseup", this.onWindowMouseUp);
    }
    this.focusHost.focus();
    this.paintCursor();
  }

  private openRowLabelEditor(step: number, anchor: DOMRect): void {
    this.hideRowTip();
    const ref = locateViewStep(this.clip.patterns, this.viewPatternIds, step);
    const pattern = ref ? findPattern(this.clip, ref.patternId) : this.pattern();
    const marker = getRowMarker(pattern, ref?.localStep ?? step);
    getLabActions()?.openRowLabel?.(step, marker, {
      left: anchor.left,
      top: anchor.top,
      bottom: anchor.bottom,
      right: anchor.right,
    });
  }

  private onTrackerContextMenu(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const stepCell = target?.closest?.("td.step");
    if (stepCell && this.table.contains(stepCell)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const head = target?.closest?.("th.track-head") as HTMLTableCellElement | null;
    if (!head || !this.table.contains(head) || target?.closest?.(".track-head-controls")) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const track = Number(head.dataset.track);
    if (!Number.isInteger(track) || track < 0 || track >= this.slotCount()) {
      return;
    }
    this.openTrackLabelEditor(track, head.getBoundingClientRect());
  }

  private openTrackLabelEditor(track: number, anchor: DOMRect): void {
    getLabActions()?.openTrackLabel?.(
      track,
      this.trackColors[track] ?? null,
      {
        left: anchor.left,
        top: anchor.top,
        bottom: anchor.bottom,
        right: anchor.right,
      },
    );
  }

  private onStepPointerOver(event: PointerEvent): void {
    const stepCell = (event.target as HTMLElement | null)?.closest?.("td.step") as
      | HTMLTableCellElement
      | null;
    if (!stepCell || !this.table.contains(stepCell) || !stepCell.classList.contains("has-label")) {
      return;
    }
    const related = event.relatedTarget as Node | null;
    if (related && stepCell.contains(related)) {
      return;
    }
    const label = stepCell.dataset.label;
    if (!label) {
      return;
    }
    this.showRowTip(stepCell, label);
  }

  private onStepPointerOut(event: PointerEvent): void {
    const stepCell = (event.target as HTMLElement | null)?.closest?.("td.step") as
      | HTMLTableCellElement
      | null;
    if (!stepCell || !this.table.contains(stepCell)) {
      return;
    }
    const related = event.relatedTarget as Node | null;
    if (related && stepCell.contains(related)) {
      return;
    }
    this.hideRowTipSoon();
  }

  private showRowTip(stepCell: HTMLElement, label: string): void {
    if (this.rowTipHideTimer !== undefined) {
      clearTimeout(this.rowTipHideTimer);
      this.rowTipHideTimer = undefined;
    }
    const mark = stepCell.closest("tr")?.style.getPropertyValue("--mark").trim() || "#e6a35a";
    this.rowTip.textContent = label;
    this.rowTip.style.setProperty("--mark", mark);
    this.rowTip.hidden = false;
    const rect = stepCell.getBoundingClientRect();
    const tipW = this.rowTip.offsetWidth;
    const tipH = this.rowTip.offsetHeight;
    let left = rect.right + 8;
    let top = rect.top + (rect.height - tipH) / 2;
    left = Math.min(left, window.innerWidth - tipW - 8);
    top = Math.min(Math.max(8, top), window.innerHeight - tipH - 8);
    this.rowTip.style.left = `${left}px`;
    this.rowTip.style.top = `${top}px`;
  }

  private hideRowTipSoon(): void {
    if (this.rowTipHideTimer !== undefined) {
      clearTimeout(this.rowTipHideTimer);
    }
    this.rowTipHideTimer = setTimeout(() => {
      this.rowTipHideTimer = undefined;
      this.hideRowTip();
    }, 40);
  }

  private hideRowTip(): void {
    if (this.rowTipHideTimer !== undefined) {
      clearTimeout(this.rowTipHideTimer);
      this.rowTipHideTimer = undefined;
    }
    this.rowTip.hidden = true;
  }

  applyRowMarker(step: number, marker: RowMarker | undefined): void {
    const ref = locateViewStep(this.clip.patterns, this.viewPatternIds, step);
    const pattern = ref ? findPattern(this.clip, ref.patternId) : undefined;
    if (!pattern || !ref || step < 0 || step >= this.stepCount()) {
      this.focus();
      return;
    }
    const prevRaw = getRowMarker(pattern, ref.localStep);
    const prev = prevRaw
      ? { label: prevRaw.label.trim(), color: clampRowMarkerColor(prevRaw.color) }
      : undefined;
    const next = marker?.label.trim()
      ? { label: marker.label.trim(), color: clampRowMarkerColor(marker.color) }
      : undefined;
    if (
      (!prev && !next)
      || (prev && next && prev.label === next.label && prev.color === next.color)
    ) {
      this.focus();
      return;
    }
    this.endTypingCoalesce();
    this.host.execute(
      new SetRowMarkerCommand(pattern.id, ref.localStep, prev, next),
      {
        beforeSelection: this.captureSelection(),
        afterSelection: this.captureSelection(),
      },
    );
    this.flushChangeNow();
    this.focus();
  }

  private onDragMove(event: MouseEvent): void {
    if (!this.dragging) {
      return;
    }
    this.dragPointer = { x: event.clientX, y: event.clientY };
    this.scrollDragNearEdges(event.clientX, event.clientY);
    this.applyDragFocus(event.clientX, event.clientY);
    if (this.canScrollTowardPointer(event.clientX, event.clientY)) {
      this.startDragScrollLoop();
    }
  }

  /**
   * Map a drag pointer via scroll geometry (not DOM hit-test).
   * Virtualized rows / pads / chrome outside the pool still resolve so selection
   * can extend while edge-scrolling.
   */
  private posFromScrollGeometry(
    clientX: number,
    clientY: number,
  ): { step: number; flat: number } | undefined {
    const steps = this.stepCount();
    if (steps <= 0) {
      return undefined;
    }
    const wrapRect = this.wrap.getBoundingClientRect();
    const theadH = this.table.tHead?.offsetHeight ?? 0;
    const y = clientY - wrapRect.top + this.wrap.scrollTop - theadH;
    const x = clientX - wrapRect.left + this.wrap.scrollLeft;
    const step = Math.max(0, Math.min(steps - 1, Math.floor(y / TRACKER_ROW_HEIGHT_PX)));
    const trackW = this.trackWidthPx();
    const stepW = this.stepWidthPx();
    const slots = this.slotCount();
    if (trackW <= 0 || slots <= 0) {
      return { step, flat: 0 };
    }
    const localX = x - stepW;
    let track: number;
    let offset: number;
    if (localX < 0) {
      track = 0;
      offset = 0;
    } else {
      track = Math.min(slots - 1, Math.floor(localX / trackW));
      const glyphW = trackW / CELL_WIDTH;
      offset = Math.max(0, Math.min(CELL_WIDTH - 1, Math.floor((localX % trackW) / glyphW)));
    }
    return { step, flat: track * CELL_WIDTH + offset };
  }

  private applyDragFocus(clientX: number, clientY: number): void {
    const pos = this.posFromScrollGeometry(clientX, clientY);
    if (!pos) {
      return;
    }
    this.selFocus = pos;
    this.cursor = this.cursorFromFlat(pos.step, pos.flat);
    this.paintCursor();
  }

  private static readonly DRAG_EDGE_ZONE_PX = 32;
  private static readonly DRAG_SCROLL_MAX_PX = 22;

  private dragEdgeScrollDelta(penetration: number): number {
    const zone = PaintController.DRAG_EDGE_ZONE_PX;
    const max = PaintController.DRAG_SCROLL_MAX_PX;
    if (penetration <= 0) {
      return 0;
    }
    // Outside the wrap counts as full speed; inside ramps by zone penetration.
    const t = Math.min(1, penetration / zone);
    return Math.max(1, Math.round(t * max));
  }

  /** Nudge scroll when the drag pointer sits in an edge zone. Returns true if scrolled. */
  private scrollDragNearEdges(clientX: number, clientY: number): boolean {
    const wrapRect = this.wrap.getBoundingClientRect();
    const theadH = this.table.tHead?.offsetHeight ?? 0;
    const stepW = this.stepWidthPx();
    const zone = PaintController.DRAG_EDGE_ZONE_PX;
    const top = wrapRect.top + theadH;
    const left = wrapRect.left + stepW;
    const bottom = wrapRect.bottom;
    const right = wrapRect.right;

    let dy = 0;
    if (clientY < top + zone) {
      dy = -this.dragEdgeScrollDelta(top + zone - clientY);
    } else if (clientY > bottom - zone) {
      dy = this.dragEdgeScrollDelta(clientY - (bottom - zone));
    }

    let dx = 0;
    if (clientX < left + zone) {
      dx = -this.dragEdgeScrollDelta(left + zone - clientX);
    } else if (clientX > right - zone) {
      dx = this.dragEdgeScrollDelta(clientX - (right - zone));
    }

    if (dx === 0 && dy === 0) {
      return false;
    }

    const viewH = this.wrap.clientHeight;
    const viewW = this.wrap.clientWidth;
    const maxScrollY = Math.max(
      0,
      this.stepCount() * TRACKER_ROW_HEIGHT_PX + theadH - viewH,
    );
    const maxScrollX = Math.max(0, this.wrap.scrollWidth - viewW);
    const nextTop = Math.min(maxScrollY, Math.max(0, this.wrap.scrollTop + dy));
    const nextLeft = Math.min(maxScrollX, Math.max(0, this.wrap.scrollLeft + dx));
    if (
      Math.abs(nextTop - this.wrap.scrollTop) < 0.5 &&
      Math.abs(nextLeft - this.wrap.scrollLeft) < 0.5
    ) {
      return false;
    }

    this.beginScrollSuppress();
    this.wrap.scrollTop = nextTop;
    this.wrap.scrollLeft = nextLeft;
    this.syncWindow();
    this.endScrollSuppress();
    return true;
  }

  /** True when edge zone wants motion and the scrollport can still move that way. */
  private canScrollTowardPointer(clientX: number, clientY: number): boolean {
    const wrapRect = this.wrap.getBoundingClientRect();
    const theadH = this.table.tHead?.offsetHeight ?? 0;
    const stepW = this.stepWidthPx();
    const zone = PaintController.DRAG_EDGE_ZONE_PX;
    const top = wrapRect.top + theadH;
    const left = wrapRect.left + stepW;
    const viewH = this.wrap.clientHeight;
    const viewW = this.wrap.clientWidth;
    const maxScrollY = Math.max(
      0,
      this.stepCount() * TRACKER_ROW_HEIGHT_PX + theadH - viewH,
    );
    const maxScrollX = Math.max(0, this.wrap.scrollWidth - viewW);

    if (clientY < top + zone && this.wrap.scrollTop > 0) {
      return true;
    }
    if (clientY > wrapRect.bottom - zone && this.wrap.scrollTop < maxScrollY - 0.5) {
      return true;
    }
    if (clientX < left + zone && this.wrap.scrollLeft > 0) {
      return true;
    }
    if (clientX > wrapRect.right - zone && this.wrap.scrollLeft < maxScrollX - 0.5) {
      return true;
    }
    return false;
  }

  private startDragScrollLoop(): void {
    if (this.dragScrollRaf !== 0) {
      return;
    }
    const tick = (): void => {
      this.dragScrollRaf = 0;
      if (!this.dragging || !this.dragPointer) {
        return;
      }
      const { x, y } = this.dragPointer;
      if (!this.canScrollTowardPointer(x, y)) {
        return;
      }
      this.scrollDragNearEdges(x, y);
      this.applyDragFocus(x, y);
      if (this.canScrollTowardPointer(x, y)) {
        this.dragScrollRaf = requestAnimationFrame(tick);
      }
    };
    this.dragScrollRaf = requestAnimationFrame(tick);
  }

  private stopDragScrollLoop(): void {
    if (this.dragScrollRaf !== 0) {
      cancelAnimationFrame(this.dragScrollRaf);
      this.dragScrollRaf = 0;
    }
    this.dragPointer = undefined;
  }

  private serializeSelection(): string {
    const bounds = this.selectionBounds();
    if (!bounds) {
      const { step, track, offset } = this.cursor;
      return this.bufferAt(step, track)[offset] ?? ".";
    }
    const lines: string[] = [];
    for (let step = bounds.minStep; step <= bounds.maxStep; step++) {
      let line = "";
      for (let flat = bounds.minFlat; flat <= bounds.maxFlat; flat++) {
        if (flat > bounds.minFlat && flat % CELL_WIDTH === 0) {
          line += "|";
        }
        const track = Math.floor(flat / CELL_WIDTH);
        const offset = flat % CELL_WIDTH;
        line += this.bufferAt(step, track)[offset] ?? ".";
      }
      lines.push(line);
    }
    return lines.join("\n");
  }

  private pasteText(text: string): void {
    const raw = text.replace(/\r/g, "").trimEnd();
    if (!raw) {
      return;
    }
    this.startRecord("edit");
    const bounds = this.selectionBounds();
    const originStep = bounds?.minStep ?? this.cursor.step;
    const originFlat = bounds?.minFlat ?? this.flatOf(this.cursor);
    const lines = raw.split("\n");
    let maxFlat = originFlat;
    let maxStep = originStep;

    for (let i = 0; i < lines.length; i++) {
      const step = originStep + i;
      if (step >= this.stepCount()) {
        break;
      }
      let flat = originFlat;
      const line = lines[i] ?? "";
      for (const char of line) {
        if (char === "|") {
          if (flat % CELL_WIDTH !== 0) {
            flat = Math.floor(flat / CELL_WIDTH) * CELL_WIDTH + CELL_WIDTH;
          }
          continue;
        }
        if (flat >= this.slotCount() * CELL_WIDTH) {
          break;
        }
        const track = Math.floor(flat / CELL_WIDTH);
        const offset = flat % CELL_WIDTH;
        this.writeGlyph(step, track, offset, char, { silent: true });
        maxFlat = Math.max(maxFlat, flat);
        maxStep = Math.max(maxStep, step);
        flat += 1;
      }
    }

    this.setSelection(
      { step: originStep, flat: originFlat },
      { step: maxStep, flat: maxFlat },
    );
    this.finishRecord("Paste");
  }

  private clearSelectionGlyphs(options?: { recordHistory?: boolean }): void {
    const bounds = this.selectionBounds();
    if (!bounds) {
      return;
    }
    if (options?.recordHistory !== false) {
      this.startRecord("edit");
    }
    for (let step = bounds.minStep; step <= bounds.maxStep; step++) {
      for (let flat = bounds.minFlat; flat <= bounds.maxFlat; flat++) {
        const track = Math.floor(flat / CELL_WIDTH);
        const offset = flat % CELL_WIDTH;
        this.writeGlyph(step, track, offset, ".", { silent: true });
      }
    }
    if (options?.recordHistory !== false) {
      this.finishRecord("Clear selection");
    }
    this.paintCursor();
  }

  private async copySelection(): Promise<void> {
    const text = this.serializeSelection();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.append(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
  }

  private async cutSelection(): Promise<void> {
    await this.copySelection();
    if (this.selectionIsRange()) {
      this.clearSelectionGlyphs();
      return;
    }
    this.startRecord("edit");
    const { step, track, offset } = this.cursor;
    this.writeGlyph(step, track, offset, ".");
    this.finishRecord("Cut");
  }

  private async pasteFromClipboard(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      this.pasteText(text);
    } catch {
      // Clipboard read may be blocked
    }
  }

  private scrollCursorIntoView(): void {
    this.ensureStepInView(this.cursor.step);
    this.ensureTrackInView(this.cursor.track);
    // Prefer manual axes — Element.scrollIntoView fights virtual spacer pads.
    const ch = this.cursorCh;
    if (!ch) {
      return;
    }
    const wrapRect = this.wrap.getBoundingClientRect();
    const chRect = ch.getBoundingClientRect();
    if (chRect.left < wrapRect.left + this.stepWidthPx()) {
      this.wrap.scrollLeft += chRect.left - (wrapRect.left + this.stepWidthPx()) - 8;
      this.syncWindow();
    } else if (chRect.right > wrapRect.right) {
      this.wrap.scrollLeft += chRect.right - wrapRect.right + 8;
      this.syncWindow();
    }
  }

  /** Bring a step into the vertical viewport (nearest), then refresh the virtual window. */
  private ensureStepInView(step: number): void {
    const view = this.wrap.clientHeight;
    const theadH = this.table.tHead?.offsetHeight ?? 0;
    const rowTop = step * TRACKER_ROW_HEIGHT_PX;
    const rowBottom = rowTop + TRACKER_ROW_HEIGHT_PX;
    const visibleTop = this.wrap.scrollTop;
    const visibleBottom = visibleTop + view - theadH;
    if (rowTop < visibleTop) {
      this.setScrollTop(rowTop);
    } else if (rowBottom > visibleBottom) {
      this.setScrollTop(Math.max(0, rowBottom - (view - theadH)));
    }
    this.syncWindow();
  }

  /** Align a step with the top of the scrollport (under the sticky header). */
  private scrollStepToTop(step: number): void {
    const view = this.wrap.clientHeight;
    const theadH = this.table.tHead?.offsetHeight ?? 0;
    const rowTop = Math.max(0, Math.round(step)) * TRACKER_ROW_HEIGHT_PX;
    const maxScroll = Math.max(0, this.stepCount() * TRACKER_ROW_HEIGHT_PX + theadH - view);
    this.setScrollTop(Math.min(maxScroll, rowTop));
    this.syncWindow();
    // Pad swaps can clamp scrollTop; re-apply once the window matches.
    if (Math.abs(this.wrap.scrollTop - Math.min(maxScroll, rowTop)) > 0.5) {
      this.setScrollTop(Math.min(maxScroll, rowTop));
      this.syncWindow();
    }
    this.ensureTrackInView(this.cursor.track);
    this.paintCursor();
    this.paintPlayhead();
  }

  /** Bring a track column into the horizontal viewport, then refresh the column window. */
  private ensureTrackInView(track: number): void {
    const trackW = this.trackWidthPx();
    const stepW = this.stepWidthPx();
    const colLeft = stepW + track * trackW;
    const colRight = colLeft + trackW;
    const viewLeft = this.wrap.scrollLeft;
    const viewRight = viewLeft + this.wrap.clientWidth;
    // Sticky step covers the left gutter — keep the target track clear of it.
    const stickyRight = viewLeft + stepW;
    if (colLeft < stickyRight) {
      this.setScrollLeft(Math.max(0, track * trackW));
    } else if (colRight > viewRight) {
      this.setScrollLeft(Math.max(0, colRight - this.wrap.clientWidth));
    }
    this.syncWindow();
  }

  private setScrollLeft(x: number): void {
    this.beginScrollSuppress();
    this.wrap.scrollLeft = x;
    this.endScrollSuppress();
  }

  private moveByOffset(delta: number, extend = false): void {
    const before = { step: this.cursor.step, flat: this.flatOf(this.cursor) };
    let { step, track, offset } = this.cursor;
    offset += delta;
    while (offset < 0) {
      offset += CELL_WIDTH;
      track -= 1;
    }
    while (offset >= CELL_WIDTH) {
      offset -= CELL_WIDTH;
      track += 1;
    }
    while (track < 0) {
      track += this.slotCount();
      step -= 1;
    }
    while (track >= this.slotCount()) {
      track -= this.slotCount();
      step += 1;
    }
    step = Math.min(this.stepCount() - 1, Math.max(0, step));
    this.cursor = { step, track, offset };
    this.syncActiveFromCursor();
    this.updateSelectionAfterMove(before, extend);
    this.paintCursor();
    this.scrollCursorIntoView();
  }

  private moveByStep(delta: number, extend = false): void {
    const before = { step: this.cursor.step, flat: this.flatOf(this.cursor) };
    this.cursor = {
      ...this.cursor,
      step: Math.min(this.stepCount() - 1, Math.max(0, this.cursor.step + delta)),
    };
    this.syncActiveFromCursor();
    this.updateSelectionAfterMove(before, extend);
    this.paintCursor();
    this.scrollCursorIntoView();
  }

  /** Jump to next/previous instrument column; wraps across rows. Keeps field offset. */
  private moveByTrack(delta: number, extend = false): void {
    const before = { step: this.cursor.step, flat: this.flatOf(this.cursor) };
    let { step, track, offset } = this.cursor;
    track += delta;
    while (track < 0) {
      track += this.slotCount();
      step -= 1;
    }
    while (track >= this.slotCount()) {
      track -= this.slotCount();
      step += 1;
    }
    step = Math.min(this.stepCount() - 1, Math.max(0, step));
    this.cursor = { step, track, offset };
    this.syncActiveFromCursor();
    this.updateSelectionAfterMove(before, extend);
    this.paintCursor();
    this.scrollCursorIntoView();
  }

  private stepsPerBar(): number {
    return Math.max(1, this.clip.stepDivision || 16);
  }

  private updateSelectionAfterMove(
    before: { step: number; flat: number },
    extend: boolean,
  ): void {
    const focus = { step: this.cursor.step, flat: this.flatOf(this.cursor) };
    if (extend) {
      if (!this.selAnchor) {
        this.selAnchor = before;
      }
      this.selFocus = focus;
    } else {
      this.selAnchor = focus;
      this.selFocus = focus;
    }
  }

  private onKeyDown(event: KeyboardEvent): void {
    const target = event.target;
    if (
      target instanceof HTMLInputElement
      || target instanceof HTMLTextAreaElement
      || target instanceof HTMLSelectElement
      || (target instanceof HTMLElement && target.isContentEditable)
    ) {
      // Header rename / other chrome inputs live inside the focus host.
      return;
    }

    const mod = event.metaKey || event.ctrlKey;

    // Undo/redo is handled globally by labRuntime (works with no focus too).
    if (mod && event.key.toLowerCase() === "c") {
      event.preventDefault();
      void this.copySelection();
      return;
    }
    if (mod && event.key.toLowerCase() === "x") {
      event.preventDefault();
      void this.cutSelection();
      return;
    }
    if (mod && event.key.toLowerCase() === "v") {
      event.preventDefault();
      void this.pasteFromClipboard();
      return;
    }
    if (mod && (event.key === "ArrowUp" || event.key === "ArrowDown"
      || event.key === "ArrowLeft" || event.key === "ArrowRight")) {
      // Handled in capture-phase onGlobalKeyDown (blocks browser history / word-jump).
      return;
    }
    if (mod || event.altKey) {
      return;
    }

    if (this.armMode && this.handleArmKeyDown(event)) {
      return;
    }

    const { step, track, offset } = this.cursor;
    const extend = event.shiftKey;

    if (event.key === "Escape") {
      event.preventDefault();
      this.clearSelection();
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.endTypingCoalesce();
      this.moveByOffset(-1, extend);
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.endTypingCoalesce();
      this.moveByOffset(1, extend);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.endTypingCoalesce();
      this.moveByStep(-1, extend);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.endTypingCoalesce();
      this.moveByStep(1, extend);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      this.endTypingCoalesce();
      const before = { step: this.cursor.step, flat: this.flatOf(this.cursor) };
      this.cursor = { step, track: 0, offset: 0 };
      this.updateSelectionAfterMove(before, extend);
      this.paintCursor();
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      this.endTypingCoalesce();
      const before = { step: this.cursor.step, flat: this.flatOf(this.cursor) };
      this.cursor = { step, track: this.slotCount() - 1, offset: CELL_WIDTH - 1 };
      this.updateSelectionAfterMove(before, extend);
      this.paintCursor();
      return;
    }
    if (event.key === "Tab") {
      event.preventDefault();
      this.endTypingCoalesce();
      let t = track + (event.shiftKey ? -1 : 1);
      let s = step;
      if (t >= this.slotCount()) {
        t = 0;
        s = Math.min(this.stepCount() - 1, s + 1);
      } else if (t < 0) {
        t = this.slotCount() - 1;
        s = Math.max(0, s - 1);
      }
      this.cursor = { step: s, track: t as InstrumentSlot, offset: 0 };
      const focus = { step: s, flat: t * CELL_WIDTH };
      this.selAnchor = focus;
      this.selFocus = focus;
      this.paintCursor();
      this.scrollCursorIntoView();
      return;
    }
    if (event.key === "Backspace") {
      event.preventDefault();
      if (this.selectionIsRange()) {
        this.clearSelectionGlyphs();
        return;
      }
      this.startRecord("type");
      this.writeGlyph(step, track, offset, ".");
      // Stay put on the first note column (don't wrap into the previous track).
      if (offset > 0) {
        this.moveByOffset(-1);
      }
      this.finishRecord();
      return;
    }
    if (event.key === "Delete") {
      event.preventDefault();
      if (this.selectionIsRange()) {
        this.clearSelectionGlyphs();
        return;
      }
      this.startRecord("type");
      this.writeGlyph(step, track, offset, ".");
      this.finishRecord();
      return;
    }
    if (event.key === ",") {
      event.preventDefault();
      this.tryRepeatLastNote();
      return;
    }
    if (event.key === ".") {
      event.preventDefault();
      this.typeChar(".");
      return;
    }
    if (event.key.length === 1 && event.key !== " ") {
      event.preventDefault();
      this.typeChar(event.key);
    }
  }

  /** Arm mode: cell-wise arrows, piano keys write full notes, Z/X nudge octave. */
  private handleArmKeyDown(event: KeyboardEvent): boolean {
    const extend = event.shiftKey;
    const key = event.key;
    const keyLower = key.length === 1 ? key.toLowerCase() : key;

    if (key === "ArrowLeft") {
      event.preventDefault();
      this.endTypingCoalesce();
      this.moveByCell(-1, 0, extend);
      return true;
    }
    if (key === "ArrowRight") {
      event.preventDefault();
      this.endTypingCoalesce();
      this.moveByCell(1, 0, extend);
      return true;
    }
    if (key === "ArrowUp") {
      event.preventDefault();
      this.endTypingCoalesce();
      this.moveByCell(0, -1, extend);
      return true;
    }
    if (key === "ArrowDown") {
      event.preventDefault();
      this.endTypingCoalesce();
      this.moveByCell(0, 1, extend);
      return true;
    }

    if (key === "Backspace" || key === "Delete") {
      event.preventDefault();
      if (this.selectionIsRange()) {
        this.clearSelectionGlyphs();
      } else {
        this.clearArmedNote();
      }
      return true;
    }

    if (keyLower === "z") {
      event.preventDefault();
      if (!event.repeat) {
        this.nudgeArmedOctave(-1);
      }
      return true;
    }
    if (keyLower === "x") {
      event.preventDefault();
      if (!event.repeat) {
        this.nudgeArmedOctave(1);
      }
      return true;
    }

    if (key === "-") {
      event.preventDefault();
      if (!event.repeat) {
        this.toggleArmedNoteOff();
      }
      return true;
    }

    const midi = pianoKeyMidi(keyLower, this.lastOctave);
    if (midi !== undefined) {
      event.preventDefault();
      if (!event.repeat) {
        this.insertArmedNote(midi, keyLower);
      }
      return true;
    }

    if (event.key === ",") {
      if (!event.repeat && this.tryRepeatLastNote()) {
        event.preventDefault();
        return true;
      }
      // Note column: no last note → swallow (same as other non-piano glyphs).
      if (fieldForOffset(this.cursor.offset) === "note") {
        event.preventDefault();
        return true;
      }
      return false;
    }

    // In the note field, swallow non-piano glyphs (vol/fx still type normally).
    if (
      event.key.length === 1
      && event.key !== " "
      && event.key !== "."
      && fieldForOffset(this.cursor.offset) === "note"
    ) {
      event.preventDefault();
      return true;
    }

    return false;
  }

  /** Move by whole tracker cells; cursor always lands on note column 0. */
  private moveByCell(dTrack: number, dStep: number, extend: boolean): void {
    this.endTypingCoalesce();
    const before = { step: this.cursor.step, flat: this.flatOf(this.cursor) };
    let { step, track } = this.cursor;
    track += dTrack;
    step += dStep;
    while (track < 0) {
      track += this.slotCount();
      step -= 1;
    }
    while (track >= this.slotCount()) {
      track -= this.slotCount();
      step += 1;
    }
    step = Math.max(0, Math.min(this.stepCount() - 1, step));
    this.cursor = { step, track: track as InstrumentSlot, offset: 0 };
    this.syncActiveFromCursor();
    this.updateSelectionAfterMove(before, extend);
    this.paintCursor();
    this.scrollCursorIntoView();
    this.notifyPositionChange();
  }

  private writeBufferToPattern(step: number, track: number, buffer: string): void {
    const ref = locateViewStep(this.clip.patterns, this.viewPatternIds, step);
    const pattern = ref ? findPattern(this.clip, ref.patternId) : undefined;
    const trackObj = pattern?.tracks[track];
    if (trackObj && ref) {
      trackObj.cells[ref.localStep] = decodeCell(buffer);
    }
  }

  private writeNoteField(
    step: number,
    track: number,
    noteToken: string,
    options?: { silent?: boolean; rememberOctave?: boolean },
  ): void {
    const from = this.bufferAt(step, track);
    const current = from.padEnd(CELL_WIDTH, ".").slice(0, CELL_WIDTH);
    const note = padField(noteToken, NOTE_WIDTH, " ");
    const buffer = (note + current.slice(NOTE_WIDTH)).padEnd(CELL_WIDTH, ".").slice(0, CELL_WIDTH);
    if (this.recording) {
      this.recordCellPatch(step, track, from, buffer);
    }
    this.applyCellBuffer(step, track, buffer, options);
  }

  /** Remember pitched notes from edits; drives octave auto-fill and `,` repeat. */
  private rememberNoteFromCell(
    cell: { note?: number; noteOff?: boolean },
    options?: { rememberOctave?: boolean },
  ): void {
    if (cell.note === undefined || cell.noteOff) {
      return;
    }
    this.lastNoteMidi = Math.max(0, Math.min(127, cell.note));
    if (options?.rememberOctave === false) {
      return;
    }
    this.lastOctave = clampNoteOctave(Math.floor(this.lastNoteMidi / 12) - 1);
  }

  /**
   * In the note field, `,` stamps the last edited note (+ octave) and advances a row.
   * No memory / range selection / non-note field → no-op (caller may fall through).
   */
  private tryRepeatLastNote(): boolean {
    if (this.lastNoteMidi === undefined) {
      return false;
    }
    if (this.selectionIsRange()) {
      return false;
    }
    if (fieldForOffset(this.cursor.offset) !== "note") {
      return false;
    }

    const { step, track } = this.cursor;
    this.startRecord("type");
    this.writeNoteField(step, track, formatNoteName(this.lastNoteMidi));
    const nextStep = Math.min(this.stepCount() - 1, step + 1);
    this.cursor = { step: nextStep, track, offset: 0 };
    this.selAnchor = { step: nextStep, flat: this.flatOf(this.cursor) };
    this.selFocus = this.selAnchor;
    this.finishRecord();
    this.paintCursor();
    this.scrollCursorIntoView();
    this.notifyPositionChange();
    return true;
  }

  private insertArmedNote(midi: number, key: string): void {
    const clamped = Math.max(0, Math.min(127, midi));
    const { step, track } = this.cursor;
    this.startRecord("type");
    this.writeNoteField(step, track, formatNoteName(clamped), {
      // Next-octave keys (K+) must not climb lastOctave — they stay relative to base.
      rememberOctave: pianoKeyRemembersOctave(key),
    });
    this.armAuditionStart(key, track, clamped);
    // Advance to the next row so the following key enters the next step.
    const nextStep = Math.min(this.stepCount() - 1, step + 1);
    this.cursor = { step: nextStep, track, offset: 0 };
    this.selAnchor = { step: nextStep, flat: this.flatOf(this.cursor) };
    this.selFocus = this.selAnchor;
    this.finishRecord();
    this.paintCursor();
    this.scrollCursorIntoView();
    this.notifyPositionChange();
  }

  /** Arm `-`: toggle note-off (`---`) on the current cell (vol/fx kept), then advance a row. */
  private toggleArmedNoteOff(): void {
    const { step, track } = this.cursor;
    const cell = decodeCell(this.bufferAt(step, track));
    this.startRecord("type");
    if (cell.noteOff) {
      this.writeNoteField(step, track, "");
    } else {
      this.writeNoteField(step, track, "---");
    }
    const nextStep = Math.min(this.stepCount() - 1, step + 1);
    this.cursor = { step: nextStep, track, offset: 0 };
    this.selAnchor = { step: nextStep, flat: this.flatOf(this.cursor) };
    this.selFocus = this.selAnchor;
    this.finishRecord();
    this.paintCursor();
    this.scrollCursorIntoView();
    this.notifyPositionChange();
  }

  /** Clear the whole tracker cell (note + vol + fx). Mac Delete is usually Backspace. */
  private clearArmedNote(): void {
    const { step, track } = this.cursor;
    this.startRecord("type");
    const from = this.bufferAt(step, track);
    const buffer = ".".repeat(CELL_WIDTH);
    this.recordCellPatch(step, track, from, buffer);
    this.applyCellBuffer(step, track, buffer, { silent: true });
    this.cursor = { step, track, offset: 0 };
    this.selAnchor = { step, flat: this.flatOf(this.cursor) };
    this.selFocus = this.selAnchor;
    this.finishRecord("Clear cell");
    this.paintCursor();
    this.notifyPositionChange();
  }

  private nudgeArmedOctave(delta: number): void {
    const { step, track } = this.cursor;
    const cell = decodeCell(this.bufferAt(step, track));
    if (cell.note !== undefined) {
      const nextMidi = cell.note + delta * 12;
      if (nextMidi < 0 || nextMidi > 127) {
        return;
      }
      this.startRecord("type");
      this.writeNoteField(step, track, formatNoteName(nextMidi));
      this.cursor = { step, track, offset: 0 };
      this.finishRecord();
      this.paintCursor();
      this.notifyPositionChange();
      return;
    }
    this.lastOctave = clampNoteOctave(this.lastOctave + delta);
  }

  private armAuditionStart(key: string, track: number, midi: number): void {
    const prev = this.armHeldNotes.get(key);
    if (prev) {
      this.options.onAuditionNote?.(prev.track, prev.midi, false);
    }
    this.armHeldNotes.set(key, { track, midi });
    this.options.onAuditionNote?.(track, midi, true);
  }

  private onArmKeyUp(event: KeyboardEvent): void {
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    const held = this.armHeldNotes.get(key);
    if (!held) {
      return;
    }
    this.armHeldNotes.delete(key);
    this.options.onAuditionNote?.(held.track, held.midi, false);
  }

  private releaseArmAudition(): void {
    for (const held of this.armHeldNotes.values()) {
      this.options.onAuditionNote?.(held.track, held.midi, false);
    }
    this.armHeldNotes.clear();
  }

  private onGlobalKeyDown(event: KeyboardEvent): void {
    const active = document.activeElement;
    const focused =
      active === this.focusHost
      || (active instanceof Node && this.focusHost.contains(active));
    if (!focused) {
      return;
    }
    const mod = event.metaKey || event.ctrlKey;
    if (!mod) {
      return;
    }
    const key = event.key;
    // Capture-phase: block browser history (⌘←/→) / word-jump and move in the grid.
    if (key === "ArrowLeft" || key === "ArrowRight") {
      event.preventDefault();
      event.stopPropagation();
      this.endTypingCoalesce();
      this.moveByTrack(key === "ArrowLeft" ? -1 : 1, event.shiftKey);
      return;
    }
    if (key === "ArrowUp" || key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      this.endTypingCoalesce();
      const bar = this.stepsPerBar();
      this.moveByStep(key === "ArrowUp" ? -bar : bar, event.shiftKey);
    }
  }

  private clearHistory(): void {
    this.host.clear();
    this.pendingPatches.clear();
    this.recording = false;
    this.editBeforeSelection = undefined;
    this.endTypingCoalesce();
  }

  private captureSelection(): AdamSelectionState {
    return {
      cursor: { ...this.cursor },
      selAnchor: this.selAnchor ? { ...this.selAnchor } : undefined,
      selFocus: this.selFocus ? { ...this.selFocus } : undefined,
    };
  }

  private applySelectionState(selection: AdamSelectionState | undefined): void {
    if (!selection) {
      return;
    }
    this.cursor = { ...selection.cursor };
    this.selAnchor = selection.selAnchor ? { ...selection.selAnchor } : undefined;
    this.selFocus = selection.selFocus ? { ...selection.selFocus } : undefined;
  }

  private createCommandContext(): AdamCommandContext {
    return {
      selection: this.captureSelection(),
      getCell: (step, track) => this.bufferAt(step, track),
      setCell: (step, track, buffer) => {
        // Command execute/undo re-applies buffers that interactive writes already
        // applied. Do not touch typing memory — otherwise next-octave arm keys
        // (K+) climb lastOctave on every finishRecord → SetCellsCommand.
        this.applyCellBuffer(step, track, buffer, {
          silent: true,
          rememberNote: false,
        });
      },
      getTrackName: track => this.pattern()?.tracks[track]?.name,
      setTrackName: (track, name) => {
        this.applyTrackNameAllPatterns(track, name);
      },
      getRowMarker: (patternId, localStep) => {
        const pattern = findPattern(this.clip, patternId);
        return pattern ? getRowMarker(pattern, localStep) : undefined;
      },
      setRowMarker: (patternId, localStep, marker) => {
        const pattern = findPattern(this.clip, patternId);
        if (pattern) {
          setRowMarker(pattern, localStep, marker);
        }
      },
    };
  }

  private commitCommandResult(
    result: AdamCommandResult,
    selection: AdamSelectionState | undefined,
  ): void {
    this.applySelectionState(selection);
    if (result.changed === "trackName" || result.changed === "tracks" || result.changed === "mixed") {
      this.refreshHeader();
    }
    if (result.changed === "rowMarker" || result.changed === "mixed") {
      this.syncWindow(true);
    }
    if (
      result.changed === "muteSolo" ||
      result.changed === "trackColor" ||
      result.changed === "tracks" ||
      result.changed === "mixed"
    ) {
      this.syncMutedFromActivePattern();
      this.paintTrackHeads();
      this.paintPatternMuteDims();
      this.emitMuteSolo();
    }

    // Host-only edits (instrument knobs, banks, FX/mix, sample library) do not
    // change the clip — do not yank the viewport back to the edit cursor (that
    // fights playhead-follow during playback).
    const hostOnly =
      result.changed === "instrument"
      || result.changed === "bank"
      || result.changed === "samples"
      || result.changed === "fx"
      || result.changed === "slotFx"
      || result.changed === "slotMix";
    if (hostOnly) {
      if (selection) {
        this.paintCursor();
        this.notifyPositionChange();
      }
      return;
    }

    this.paintCursor();
    this.scrollCursorIntoView();
    this.notifyPositionChange();
    this.scheduleChange();
  }

  private startRecord(kind: "type" | "edit"): void {
    if (this.recording) {
      this.finishRecord();
    }
    this.editBeforeSelection = this.captureSelection();
    this.pendingPatches.clear();
    this.recording = true;
    if (kind === "type") {
      if (!this.typingCoalesce) {
        // New coalesce window — already advanced by endTypingCoalesce when idle ends.
      }
      this.patchMergeGen = this.typingGen;
      this.bumpTypingCoalesce();
    } else {
      this.patchMergeGen = undefined;
      this.endTypingCoalesce();
    }
  }

  private finishRecord(label?: string): void {
    if (!this.recording) {
      return;
    }
    this.recording = false;
    const patches = [...this.pendingPatches.values()].filter(p => p.from !== p.to);
    this.pendingPatches.clear();
    const before = this.editBeforeSelection;
    this.editBeforeSelection = undefined;
    if (patches.length === 0) {
      return;
    }
    this.host.execute(
      new SetCellsCommand(patches, {
        label: label ?? (this.patchMergeGen !== undefined ? "Type" : "Edit cells"),
        mergeGen: this.patchMergeGen,
      }),
      {
        beforeSelection: before,
        afterSelection: this.captureSelection(),
      },
    );
  }

  private bumpTypingCoalesce(): void {
    this.typingCoalesce = true;
    if (this.typingCoalesceTimer !== undefined) {
      clearTimeout(this.typingCoalesceTimer);
    }
    this.typingCoalesceTimer = setTimeout(() => {
      this.endTypingCoalesce();
    }, 600);
  }

  private endTypingCoalesce(): void {
    if (this.typingCoalesce) {
      this.typingGen += 1;
    }
    this.typingCoalesce = false;
    if (this.typingCoalesceTimer !== undefined) {
      clearTimeout(this.typingCoalesceTimer);
      this.typingCoalesceTimer = undefined;
    }
  }

  private prepareHistoryNav(): void {
    this.endTypingCoalesce();
    if (this.recording) {
      this.finishRecord();
    }
  }

  private typeChar(raw: string): void {
    const bounds = this.selectionBounds();
    if (bounds && this.selectionIsRange()) {
      this.startRecord("edit");
      for (let step = bounds.minStep; step <= bounds.maxStep; step++) {
        for (let flat = bounds.minFlat; flat <= bounds.maxFlat; flat++) {
          const track = Math.floor(flat / CELL_WIDTH) as InstrumentSlot;
          const offset = flat % CELL_WIDTH;
          // Pure glyph fill — no note-letter auto-octave (that would fight multi-col selections).
          this.writeTypedGlyph(step, track, offset, raw, { silent: true, autoOctave: false });
        }
      }
      this.finishRecord("Fill selection");
      this.paintCursor();
      return;
    }

    this.startRecord("type");
    const { step, track, offset } = this.cursor;
    this.writeTypedGlyph(step, track, offset, raw);
    this.moveByOffset(1);
    this.finishRecord();
  }

  /**
   * Apply one typed character at a glyph cell (casing + optional note-letter auto octave).
   * Used for normal typing and for fill-selection.
   */
  private writeTypedGlyph(
    step: number,
    track: InstrumentSlot,
    offset: number,
    raw: string,
    options?: { silent?: boolean; autoOctave?: boolean },
  ): void {
    const field = fieldForOffset(offset);
    let char = raw;
    if (field === "note") {
      char = offset === 0 ? raw.toUpperCase() : raw;
    } else {
      char = raw.toUpperCase();
    }
    const glyph = char.slice(0, 1);

    if (field === "note" && offset >= 2 && /^\d$/.test(glyph)) {
      this.lastOctave = clampNoteOctave(Number(glyph));
    }

    const autoOctave = options?.autoOctave !== false;
    const before = field === "note" && autoOctave ? this.bufferAt(step, track) : "";
    this.writeGlyph(step, track, offset, glyph, { silent: options?.silent });

    // Typing a note letter on a cell without octave → insert "-{lastOctave}".
    if (
      autoOctave
      && field === "note"
      && offset === 0
      && isNoteLetter(glyph)
      && !noteFieldHasOctave(before)
    ) {
      const octave = String(clampNoteOctave(this.lastOctave));
      this.writeGlyph(step, track, 1, "-", { silent: true });
      this.writeGlyph(step, track, 2, octave, { silent: true });
    }
  }
}
